<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">{{ pageTitle }}</h4>
            <b-button size="sm" variant="outline-dark" class="ml-2 mr-auto" @click="$refs.photoQueue && $refs.photoQueue.openQr()">📱 手机拍照二维码<small>(핸드폰 촬영 QR)</small></b-button>
            <!-- 入库处理 / 已跟客户确认(待客户确认 에서 처리 완료된 건) 탭 — 2026-10-04 YZY -->
            <div class="btn-group btn-group-sm">
              <button type="button" :class="['btn', mainTab === 'import' ? 'btn-primary' : 'btn-outline-primary']" @click="mainTab = 'import'">入库处理<small>(입고처리)</small></button>
              <button type="button" :class="['btn', mainTab === 'confirmed' ? 'btn-primary' : 'btn-outline-primary']" @click="mainTab = 'confirmed'">
                已跟客户确认<small>(고객확인 완료)</small>
                <b-badge v-if="confirmedNew > 0" variant="danger" pill class="ml-1">{{ confirmedNew }}</b-badge>
              </button>
            </div>
          </div>
          <div class="card-body">
            <confirmed-import-list v-show="mainTab === 'confirmed'" :active="mainTab === 'confirmed'" @new-count="confirmedNew = $event" @open-order="openConfirmedOrder"></confirmed-import-list>
            <div v-show="mainTab === 'import'">
            <!-- 검색 (운송장번호 / 주문번호) -->
            <div class="row mb-3">
              <div class="col-md-8">
                <div class="input-group">
                  <b-form-select v-model="searchType" :options="searchTypeOptions" style="max-width:200px" size="sm" class="border-right-0"></b-form-select>
                  <input type="text" class="form-control" :placeholder="{ tracking: '扫描/输入快递单号(운송장번호 스캔/입력)', order: '输入货主订单号(화주주문번호 입력)', order1688: '输入1688订单号(1688주문번호 입력)' }[searchType]" v-model="searchKeyword" @keyup.enter="doSearch">
                  <div class="input-group-append">
                    <b-button variant="primary" @click="doSearch" :disabled="loading">
                      <b-icon icon="search"></b-icon> 搜索<small class="ml-1">(검색)</small>
                    </b-button>
                    <b-button variant="outline-secondary" @click="openManualPrint">
                      🖨️ 手动打印<small class="ml-1">(수동출력)</small>
                    </b-button>
                  </div>
                </div>
              </div>
              <div class="col-md-4 d-flex align-items-center" v-if="searchResults.length">
                <b-badge variant="info" class="p-2 mr-2">搜索结果<small>(검색결과)</small>: {{ searchResults.length }}件</b-badge>
                <b-button size="sm" variant="outline-primary" @click="openWorkModal">
                  <b-icon icon="tools"></b-icon> 开始作业<small class="ml-1">(작업시작)</small>
                </b-button>
                <b-button size="sm" variant="success" @click="openBoxAssign" class="ml-1">
                  📦 箱子分配<small class="ml-1">(박스배정)</small>
                </b-button>
              </div>
            </div>

            <!-- 로딩 -->
            <div v-if="loading" class="text-center my-4">
              <b-spinner variant="primary"></b-spinner>
            </div>

            <!-- 검색 결과 테이블 -->
            <div v-if="searchResults.length && !loading" class="table-responsive">
              <table class="table table-sm table-bordered table-hover">
                <thead class="thead-light">
                  <tr>
                    <th style="width:5%"><input type="checkbox" v-model="checkAll" @change="toggleAll"></th>
                    <th style="width:8%">图片<small>(이미지)</small></th>
                    <th style="width:20%">商品名<small>(상품명)</small></th>
                    <th>条码<small>(바코드)</small></th>
                    <th>货主<small>(업체)</small></th>
                    <th style="width:12%">采购数量<small>(구매수량)</small></th>
                    <th style="width:10%">入库数量<small>(입고수량)</small></th>
                    <th>状态<small>(상태)</small></th>
                    <th style="width:15%">处理<small>(처리)</small></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in searchResults" :key="item._id"
                      :class="{'table-warning': item._selected, 'table-success': item._done}">
                    <!-- 같은 구매요청에서 나뉜 행은 왼쪽 선으로 묶어 보인다(원본 행 = 굵은 선, 조각 = 얇은 선) -->
                    <td :style="groupLineStyle(item)"><input type="checkbox" v-model="item._selected"></td>
                    <td>
                      <img v-if="item.옵션이미지 || item.등록이미지" :src="cdnImg(item.옵션이미지 || item.등록이미지)" style="width:50px;height:50px;object-fit:cover" class="rounded cursor-pointer" @click="openWorkModalAt(idx)">
                      <span v-else>-</span>
                    </td>
                    <td class="small">
                      {{ item.상품명 }}
                      <div v-if="item.옵션1_중국어 || item.옵션2_중국어" style="font-size:0.9rem;color:#212529;font-weight:600">
                        {{ item.옵션1_중국어 }}<span v-if="item.옵션2_중국어"> / {{ item.옵션2_중국어 }}</span>
                      </div>
                    </td>
                    <td class="small">{{ item.바코드 }}</td>
                    <td class="small">{{ item.companyid }}</td>
                    <td class="text-center">{{ purchaseTotalQty(item) }} <small class="text-muted">({{ item.구매요청수량 }}x<span style="color:#ff0000;font-weight:900">{{ composeQty(item) }}P</span>)</small></td>
                    <td>
                      <input type="number" class="form-control form-control-sm" v-model.number="item._입고수량" min="0">
                    </td>
                    <td>
                      <b-badge :variant="statusVariant(item.processStatus)" :title="item.processStatus">{{ itemStatusText(item) }}<template v-if="isGroupPiece(item)"> {{ item.구매요청수량 }}</template></b-badge>
                      <small v-if="isGroupPiece(item)" class="d-block text-muted">拆分(분할)</small>
                      <!-- 묶음 요약(입고/미도착/오류 수량)은 원본(남은 수량) 행에만 한 번 -->
                      <div v-if="groupBadges(item).length" class="mt-1" style="line-height:1.6">
                        <b-badge v-for="b in groupBadges(item)" :key="b.key" :variant="b.variant" class="mr-1">{{ b.text }}</b-badge>
                      </div>
                    </td>
                    <td>
                      <b-button size="sm" variant="outline-primary" @click="openWorkModalAt(idx)" class="mr-1">
                        <b-icon icon="eye"></b-icon>
                      </b-button>
                      <template v-if="item.processStatus === '입고오류'">
                        <b-button size="sm" variant="warning" @click="openErrorModal(item)" :disabled="loading">处理</b-button>
                        <!-- 입고 때 등록한 오류 내용·수량을 나중에 고친다(2026-10-16 SYL #33). 서버(import/error/edit)가 등록자 본인·같은 그룹 조장만 허용 -->
                        <b-button size="sm" variant="outline-secondary" class="ml-1" @click="openErrEdit(item)" :disabled="loading">修改<small>(입고오류 수정)</small></b-button>
                      </template>
                      <!-- 입고 조정으로 처리한 건 — 고른 처리를 보고 되돌릴 수 있다 -->
                      <template v-else-if="item.입고오류상세 && item.입고오류상세.조정">
                        <b-button size="sm" variant="outline-info" @click="openErrorModal(item)" :disabled="loading">调整</b-button>
                      </template>
                      <template v-else-if="item.splitGroupId && ['출고대기', '중국재고', '재고대기', '출고확인대기'].includes(item.processStatus)">
                        <b-button size="sm" variant="outline-warning" @click="revertSplit(item)" :disabled="loading">↩ 되돌리기</b-button>
                      </template>
                      <!-- 이미 입고된 건(출고대기·재고대기·중국재고 등 입고 이후 상태)은 입고·오류 버튼을 숨긴다 -->
                      <template v-else-if="isInboundDone(item)">
                        <span class="small text-muted mr-1">{{ inboundDoneText(item) }}</span>
                        <!-- 입고 뒤 불량: 출고대기 + 박스 미할당 줄만 错误 등록 허용(재고 원장 그대로, 처리상태만 입고오류) -->
                        <b-button v-if="canRegisterErrorAfterInbound(item)" size="sm" variant="danger" @click="openErrorModal(item)" :disabled="loading" class="ml-1">错误</b-button>
                      </template>
                      <template v-else>
                        <b-button size="sm" variant="success" @click="processImport([item])" :disabled="loading">入库</b-button>
                        <b-button size="sm" variant="danger" @click="openErrorModal(item)" :disabled="loading" class="ml-1">错误</b-button>
                      </template>
                      <import-photo-panel :item="item" compact class="mt-1"></import-photo-panel>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 일괄 입고처리 버튼 -->
            <div v-if="selectedItems.length" class="mt-3">
              <b-button variant="primary" @click="processImport(selectedItems)" :disabled="loading">
                📦 选中 {{ selectedItems.length }}件 批量入库<small class="ml-1">(일괄 입고처리)</small>
              </b-button>
              <!-- 고른 건 전부에 같은 오류(유형·내용·사진)를 한 번에 등록 — 건마다 기존 등록 API 를 차례로 부른다(#127) -->
              <b-button variant="danger" class="ml-2" @click="openBulkError" :disabled="loading">
                ⚠ 选中 {{ selectedItems.length }}件 批量登记错误<small class="ml-1">(일괄 오류 등록)</small>
              </b-button>
            </div>

            <!-- 검색 결과 없음 -->
            <div v-if="!loading && searched && !searchResults.length" class="text-center text-muted my-5">
              <b-icon icon="inbox" font-scale="3"></b-icon>
              <p class="mt-2">未找到相关采购请求<small class="d-block">(해당 검색어로 검색된 구매요청이 없습니다)</small></p>
            </div>

            <!-- 핸드폰 入库拍照 대기열 — 누르면 그 주문을 위에 연다(2026-10-07 YZY) -->
            <import-photo-queue-list ref="photoQueue" :active="mainTab === 'import'" :active-id="photoQueueActive ? photoQueueActive._id : ''"
              @open="openPhotoQueue" @done="onPhotoQueueDone"></import-photo-queue-list>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== 박스배정 모달 ==================== -->
    <box-assign-modal :show="showBoxModal" :items="boxAssignItems" :companyid="boxAssignCompanyid"
      @done="onBoxAssignDone" @close="showBoxModal = false"></box-assign-modal>

    <!-- ==================== 작업 모달 ==================== -->
    <b-modal v-model="showWorkModal" hide-footer title=" " body-class="p-0 d-flex flex-column" @hidden="onWorkModalHidden" dialog-class="modal-fullscreen">
      <template #modal-header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <span>📦 {{ { tracking: '快递单号(운송장)', order: '货主订单号(화주주문번호)', order1688: '1688订单号(1688주문번호)' }[searchType] }}: <strong>{{ searchKeyword }}</strong></span>
          <span>共 {{ searchResults.length }}件 | 已处理<small>(처리)</small>: {{ doneCount }}/{{ searchResults.length }}</span>
          <b-button size="sm" variant="outline-secondary" @click="showWorkModal = false">关闭</b-button>
        </div>
      </template>
      <div class="d-flex" style="flex:1;overflow:hidden">
        <!-- 좌측: 아이템 목록 -->
        <div style="width:220px;min-width:220px;border-right:1px solid #dee2e6;display:flex;flex-direction:column">
          <div class="p-2 border-bottom d-flex align-items-center" style="flex-shrink:0">
            <strong class="small">📋 商品列表 ({{ searchResults.length }})</strong>
          </div>
          <div style="overflow-y:auto;flex:1">
            <div v-for="(item, idx) in searchResults" :key="item._id"
                 class="p-1 border-bottom cursor-pointer d-flex align-items-center"
                 :class="{'border-left border-primary': workIndex === idx, 'bg-success-light': item._done && workIndex !== idx}"
                 :style="workIndex === idx ? 'border-left:3px solid #007bff;background:#f0f7ff' : item._done ? 'background:#f0fff0' : ''"
                 @click="workIndex = idx" style="cursor:pointer">
              <img v-if="item.옵션이미지 || item.등록이미지" :src="cdnImg(item.옵션이미지 || item.등록이미지)" style="width:40px;height:40px;object-fit:cover;min-width:40px" class="rounded mr-2">
              <div v-else style="width:40px;height:40px;min-width:40px;background:#eee" class="rounded mr-2 d-flex align-items-center justify-content-center"><small class="text-muted">-</small></div>
              <div style="overflow:hidden;line-height:1.2">
                <div class="text-truncate" style="font-size:0.75rem;font-weight:500">
                  <span v-if="item._done" class="mr-1">✅</span>{{ item.상품명 ? item.상품명.slice(0, 20) : '-' }}
                </div>
                <div style="font-size:0.65rem;color:#888">{{ item.companyid }} | {{ item.구매요청수량 }}개</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 우측: 현재 작업 상품 -->
        <div class="flex-grow-1 d-flex" style="overflow:hidden" v-if="currentItem">
          <!-- 좌측: 큰 이미지 -->
          <div class="d-flex flex-column align-items-center justify-content-center p-3 border-right" style="width:50%;min-width:50%;background:#fafafa">
            <a :href="currentItem.구매링크" target="_blank" rel="noopener" v-if="currentItem.옵션이미지 || currentItem.등록이미지" class="d-block w-100 text-center">
              <img :src="cdnImg(currentItem.옵션이미지 || currentItem.등록이미지)" class="rounded border" style="max-width:100%;max-height:calc(98vh - 120px);object-fit:contain;cursor:pointer">
              <small class="d-block text-muted mt-1" v-if="currentItem.구매링크">点击查看1688商品页<small>(클릭하면 1688 상품페이지)</small></small>
            </a>
            <div v-else class="w-100 d-flex align-items-center justify-content-center" style="height:300px">
              <b-icon icon="image" font-scale="4" class="text-muted"></b-icon>
            </div>
            <b-badge :variant="statusVariant(currentItem.processStatus)" class="mt-2 p-2" style="font-size:0.9rem" :title="currentItem.processStatus">{{ itemStatusText(currentItem) }}</b-badge>
            <div v-if="groupBadges(currentItem).length" class="mt-1 text-center">
              <b-badge v-for="b in groupBadges(currentItem)" :key="b.key" :variant="b.variant" class="mr-1 p-1" style="font-size:0.85rem">{{ b.text }}</b-badge>
            </div>
            <div v-if="currentItem.옵션1_중국어 || currentItem.옵션2_중국어" class="mt-2 text-center small text-muted">
              <div v-if="currentItem.옵션1_중국어">中文选项1: {{ currentItem.옵션1_중국어 }}</div>
              <div v-if="currentItem.옵션2_중국어">中文选项2: {{ currentItem.옵션2_중국어 }}</div>
            </div>
          </div>

          <!-- 우측: 상품 정보 + 작업 -->
          <div class="p-3" style="width:50%;overflow-y:auto;font-size:1.05rem">
            <h4 class="mb-2">{{ currentItem.상품명 }}</h4>
            <div class="mb-1" v-if="currentItem.item && currentItem.item.subject"><strong>1688中文品名:</strong> {{ currentItem.item.subject }}</div>
            <div class="mb-1" v-if="currentItem.옵션1_중국어 || currentItem.옵션2_중국어"><strong>中文选项:</strong> {{ currentItem.옵션1_중국어 }}<span v-if="currentItem.옵션2_중국어"> / {{ currentItem.옵션2_중국어 }}</span></div>
            <div class="mb-1"><strong>条码:</strong> {{ currentItem.바코드 }} &nbsp; <strong>SKU:</strong> {{ currentItem.SKUID || currentItem._skuFromProductdb || '-' }}</div>
            <div class="mb-1"><strong>货主:</strong> {{ currentItem.companyid }} &nbsp; <strong>采购数量:</strong> {{ purchaseTotalQty(currentItem) }} <small class="text-muted">({{ currentItem.구매요청수량 }}x<span style="color:#ff0000;font-weight:900">{{ composeQty(currentItem) }}P</span>)</small> &nbsp; <strong>销售组合:</strong> <span style="color:#ff0000;font-weight:900">{{ composeQty(currentItem) }}P</span></div>
            <div class="mb-3 d-flex align-items-center">
              <strong>入库数量:</strong>
              <input type="number" class="form-control d-inline-block ml-2" style="width:100px;font-size:1.1rem" v-model.number="currentItem._입고수량" min="0">
            </div>

            <!-- 주문 상세정보 -->
            <div class="border rounded p-2 mb-2" v-if="currentItem.주문정보">
              <div class="font-weight-bold mb-1">📋 订单信息</div>
              <div v-if="currentItem.화주주문번호"><strong>货主订单号:</strong> {{ currentItem.화주주문번호 }}</div>
              <div v-if="currentItem.주문정보['1688주문번호']"><strong>1688订单号:</strong> {{ currentItem.주문정보['1688주문번호'] }}</div>
              <div v-if="currentItem.주문정보.주문수량 && currentItem.주문정보.주문수량 !== (parseInt(currentItem.구매요청수량) || 0) * (parseInt(currentItem.판매구성수량) || 1)">
                <b-badge variant="warning">下单数量: {{ currentItem.주문정보.주문수량 }}</b-badge>
                <small class="text-muted ml-1">(要求: {{ (parseInt(currentItem.구매요청수량) || 0) * (parseInt(currentItem.판매구성수량) || 1) }})</small>
              </div>
              <div v-if="currentItem.주문정보.판매자메시지" class="mt-1 text-info">
                <strong>卖家留言:</strong> {{ currentItem.주문정보.판매자메시지 }}
              </div>
            </div>

            <!-- 요청내역 체크리스트 -->
            <div class="border rounded p-2 mb-2" v-if="currentItem.요청내역">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="font-weight-bold">📋 要求事项</span>
                <b-button v-if="hasReqContent" size="sm" variant="outline-info" @click="translateReqContent" :disabled="reqTranslating">
                  <b-spinner v-if="reqTranslating" small></b-spinner> 🌐 翻译
                </b-button>
              </div>
              <div class="d-flex flex-wrap" style="gap:5px">
                <span v-for="c in reqChips" :key="c.key" class="badge p-2" style="font-size:0.9rem"
                      :class="c.on ? (c.basic ? 'badge-primary' : 'badge-warning') : 'badge-light'">{{ c.text }}</span>
              </div>
              <div class="mt-2" v-if="currentItem.요청내역.exportType || currentItem.요청내역.destination">
                出库形态: <strong>{{ currentItem.요청내역.exportType || '-' }}</strong> | 目的地: <strong>{{ currentItem.요청내역.destination || '-' }}</strong>
              </div>
              <!-- 샘플검증 → 寄到客户地址 건: 화주주소·통관정보(2026-10-04 YZY) -->
              <div class="mt-2 p-2 border rounded border-warning" v-if="isSampleSend(currentItem)">
                <div><strong>🧪 样品寄客户地址(화주주소):</strong>
                  <span v-if="sampleAddrText(currentItem)">{{ sampleAddrText(currentItem) }}</span>
                  <span v-else class="text-warning">地址见其他要求(주소는 기타요청사항 참조)</span>
                </div>
                <div><strong>申报英文名(신고영문명):</strong> {{ currentItem.신고영문명 || '-' }} &nbsp; <strong>HS编码:</strong> {{ currentItem.HSCODE || '-' }}</div>
              </div>
              <div class="mt-2 text-info" v-if="currentItem.요청내역.reqcontent && currentItem.요청내역.reqcontent.trim()">
                📝 <strong>其他(物流):</strong> {{ currentItem.요청내역.reqcontent }}
                <div v-if="currentItem.요청내역.reqcontent_cn && currentItem.요청내역.reqcontent_cn.trim()" class="text-success ml-3">→ {{ currentItem.요청내역.reqcontent_cn }}</div>
              </div>
              <div class="mt-1 text-info" v-if="currentItem.요청내역.reqcontentPurchase && currentItem.요청내역.reqcontentPurchase.trim()">
                📝 <strong>其他(采购):</strong> {{ currentItem.요청내역.reqcontentPurchase }}
                <div v-if="currentItem.요청내역.reqcontentPurchase_cn && currentItem.요청내역.reqcontentPurchase_cn.trim()" class="text-success ml-3">→ {{ currentItem.요청내역.reqcontentPurchase_cn }}</div>
              </div>
              <div class="mt-1 text-info" v-if="currentItem.요청사항 && currentItem.요청사항.trim() && currentItem.요청사항.trim() !== '등록완료'">📝 {{ currentItem.요청사항 }}</div>
            </div>

            <!-- 🧵 需跟客户确认(고객 확인 필요 사항, 중국어로 적는다) / 实际追加作业内容 — 2026-10-03 YZY
                 저장 필드: 요청내역.workMemo(예전 追加作业内容 칸 그대로) · workMemo_ko(한국어 번역) · workMemoActual(새 칸) -->
            <div class="border rounded p-2 mb-2">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <strong>🧵 需跟客户确认<small class="text-muted">(고객 확인 필요 사항)</small></strong>
                <div class="d-flex" style="gap:4px">
                  <!-- 한국어 문안 버튼은 入库列表 「待客户确认」 로 옮겼다(2026-10-04 YZY) -->
                  <b-button size="sm" variant="primary" @click="saveWorkMemo" :disabled="memoSaving || !workMemoText.trim()">
                    <b-spinner v-if="memoSaving" small></b-spinner> 💾 保存
                  </b-button>
                </div>
              </div>
              <textarea class="form-control form-control-sm" rows="2" v-model="workMemoText" placeholder="填写中文(중국어로 입력)"></textarea>
              <div v-if="workMemoKo && workMemoKo.trim()" class="text-primary small mt-1">🇰🇷 {{ workMemoKo }}</div>
              <div v-if="workMemoCn && workMemoCn.trim()" class="text-success small mt-1">→ {{ workMemoCn }}</div>
              <!-- 확인 필요 사진 — 입고사진과 같은 핸드폰 촬영(QR). 종류 「확인사진」 으로 쌓여 화주 화면에도 같이 보인다(2026-10-04 YZY) -->
              <import-photo-panel :item="currentItem" kind="확인사진" only-kind class="mt-2"></import-photo-panel>
              <div class="d-flex align-items-center justify-content-between mt-2 mb-1">
                <strong>🛠️ 实际追加作业内容<small class="text-muted">(실제 추가작업 내용)</small></strong>
                <b-button size="sm" variant="primary" @click="saveWorkMemoActual" :disabled="memoSaving || !workMemoActual.trim()">
                  <b-spinner v-if="memoSaving" small></b-spinner> 💾 保存
                </b-button>
              </div>
              <textarea class="form-control form-control-sm" rows="2" v-model="workMemoActual" placeholder="填写实际进行的追加作业(실제 진행한 추가작업)"></textarea>
            </div>

            <!-- 🖨️ 바코드 출력 섹션 -->
            <div class="border rounded p-2 mb-2" v-if="currentItem.바코드">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <strong>🖨️ 条码打印</strong>
                <div class="d-flex align-items-center" style="gap:4px;font-size:0.9rem">
                  <span>数量</span><input type="number" class="form-control form-control-sm" style="width:90px" v-model.number="printQty" min="1">
                  <span>间距</span><input type="number" class="form-control form-control-sm" style="width:50px" v-model.number="printGap" min="0"><span>px</span>
                  <span>字号</span><input type="number" class="form-control form-control-sm" style="width:50px" v-model.number="printFontSize" min="50" max="150"><span>%</span>
                  <b-button size="sm" variant="primary" @click="printBarcode">🖨️ 打印</b-button>
                  <b-button size="sm" variant="outline-info" @click="showCustomPrint = !showCustomPrint">📝 修改</b-button>
                </div>
              </div>
              <!-- 韩文标签资料 에 등록된 상품 — 등록한 식검/KC 표시사항으로 바로 출력(2026-10-07 YZY) -->
              <div v-if="masterLabel" class="alert alert-success py-1 px-2 mb-1 d-flex align-items-center flex-wrap" style="gap:6px">
                <span>🏷️ 已登记韩文标示(한글표시사항 등록됨)</span>
                <b-button v-if="masterLabel.food" size="sm" variant="success" @click="printMasterLabel('food')">🖨️ 打印食检韩文标签(식검 라벨 출력)</b-button>
                <b-button v-if="masterLabel.kc" size="sm" variant="info" @click="printMasterLabel('kc')">🖨️ 打印KC韩文标签(KC 라벨 출력)</b-button>
                <b-button v-if="masterLabel.hyg" size="sm" variant="secondary" @click="printMasterLabel('hyg')">🖨️ 打印卫生检疫标签(위생검역 라벨 출력)</b-button>
                <small v-if="masterLabel.kcFiles && masterLabel.kcFiles.length" class="text-muted">KC证书 {{ masterLabel.kcFiles.length }}</small>
              </div>
              <div class="d-flex align-items-center flex-wrap" style="gap:8px;font-size:0.95rem">
                <b-form-checkbox v-model="printOpts.sku" switch>SKU</b-form-checkbox>
                <b-form-checkbox v-model="printOpts.kc" switch>KC</b-form-checkbox>
                <b-form-checkbox v-model="printOpts.food" switch>食品用</b-form-checkbox>
                <b-form-checkbox v-model="printOpts.barcode" switch>条码</b-form-checkbox>
                <b-form-checkbox v-model="printOpts.label" switch>韩文标签</b-form-checkbox>
                <a v-if="currentItem.한글표시사항" href="#" @click.prevent="showLabelDetail = !showLabelDetail" class="ml-1">
                  {{ showLabelDetail ? '▲ 收起' : '▼ 详情' }}
                </a>
              </div>
              <b-collapse id="collapse-label" v-model="showLabelDetail">
                <div class="border rounded p-2 mt-1 bg-light" v-if="currentItem.한글표시사항">
                  <div v-for="(val, key) in currentItem.한글표시사항" :key="key" class="mb-1">
                    <strong>{{ key }}:</strong> {{ val || '-' }}
                  </div>
                </div>
              </b-collapse>
              <div v-if="showCustomPrint" class="mt-1">
                <textarea class="form-control form-control-sm" rows="3" v-model="customPrintContent" placeholder="修改打印内容"></textarea>
                <b-button size="sm" variant="success" class="mt-1" @click="printCustom">修改内容打印</b-button>
              </div>
            </div>

            <!-- 📱 입고사진(핸드폰 촬영) + 사이즈 -->
            <div class="border rounded p-2 mb-2"><import-photo-panel :item="currentItem"></import-photo-panel></div>

            <!-- 입고처리 버튼 -->
            <div class="d-flex flex-wrap mt-3" style="gap:8px">
              <template v-if="currentItem && currentItem.processStatus === '입고오류'">
                <b-button variant="warning" @click="openErrorModal(currentItem)" :disabled="loading" size="lg">
                  🔧 错误处理
                </b-button>
              </template>
              <span v-else-if="isInboundDone(currentItem)" class="text-muted align-self-center">{{ inboundDoneText(currentItem) }}<template v-if="currentItem.processStatus !== '출고확인대기'"> — {{ itemStatusText(currentItem) }}</template></span>
              <template v-else>
                <b-button variant="success" @click="processImportAndNext([currentItem])" :disabled="loading" size="lg">
                  ✅ 入库完成
                </b-button>
                <b-button variant="danger" @click="openErrorModal(currentItem)" :disabled="loading" size="lg">
                  ❌ 入库错误
                </b-button>
                <!-- 화주 확인이 필요한 건 — 입고는 하되 출고확인대기(入库列表 「待客户确认」 탭)로 보낸다 -->
                <b-button variant="warning" @click="processImportAndNext([currentItem], '출고확인대기')" :disabled="loading" size="lg">
                  ⏳ 待客户确认
                </b-button>
              </template>
              <b-button variant="outline-secondary" @click="moveToNextUnprocessed" :disabled="!hasNextUnprocessed" size="lg">
                下一个 ▶
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <!-- 모달 하단 -->
      <div class="border-top p-2 d-flex justify-content-between align-items-center" style="flex-shrink:0">
        <b-button variant="primary" @click="processImport(searchResults.filter(i => !i._done))" :disabled="loading">
          📦 全部批量入库
        </b-button>
        <div>
          <b-button variant="outline-primary" @click="printAllBarcodes" class="mr-2">
            🖨️ 全部条码打印
          </b-button>
          <b-button variant="outline-secondary" @click="showWorkModal = false">关闭</b-button>
        </div>
      </div>
    </b-modal>

    <!-- ==================== 입고오류 수정 모달 (2026-10-16 SYL #33) ==================== -->
    <b-modal v-model="showErrEdit" title="入库错误修改(입고오류 수정)" size="md" no-close-on-backdrop>
      <div v-if="errEditTarget" class="mb-2 p-2 bg-light rounded small">
        <strong>{{ errEditTarget.상품명 }}</strong> | {{ errEditTarget.바코드 }} | 数量<small>(수량)</small>: {{ errEditTarget.구매요청수량 }}
      </div>
      <div class="form-group">
        <label>错误数量<small>(오류 수량)</small></label>
        <input type="number" min="1" step="1" class="form-control" v-model.number="errEditForm.오류수량" :disabled="!!(errEditTarget && errEditTarget.부분입고원본id)">
        <small v-if="errEditTarget && errEditTarget.부분입고원본id" class="text-muted">已拆分的错误不能改数量，请删除后重新登记(부분 분리 건은 수량을 못 고칩니다 — 삭제 후 다시 등록)</small>
      </div>
      <div class="form-group">
        <label>错误内容<small>(오류내용)</small></label>
        <textarea class="form-control" v-model="errEditForm.오류내용" rows="4" maxlength="2000"></textarea>
      </div>
      <small class="text-muted">登记人本人或本组组长才能修改(등록자 본인 또는 같은 그룹 조장만 수정할 수 있습니다)</small>
      <template #modal-footer>
        <b-button variant="secondary" @click="showErrEdit = false">关闭(닫기)</b-button>
        <b-button variant="primary" :disabled="errEditing" @click="confirmErrEdit">保存(저장)</b-button>
      </template>
    </b-modal>

    <!-- ==================== 입고오류 모달 ==================== -->
    <b-modal v-model="showErrorModal" :title="errorMode === 'register' ? '入库错误登记(입고오류 등록)' : '入库错误处理(입고오류 처리)'" size="lg" no-close-on-backdrop>
      <div v-if="false" class="mb-3 p-2 bg-light rounded small">
        <strong>批量登记错误(일괄 오류 등록) {{ bulkTargets.length }}件</strong> — 아래 내용이 모든 건에 같이 들어가고, 수량은 건마다 전체 수량입니다(일부 수량만 오류면 건별로 등록하세요)
        <ul class="mb-0 pl-3"><li v-for="t in bulkTargets" :key="'bt' + t._id">{{ t.상품명 }} | {{ t.바코드 }} | {{ t.구매요청수량 }}</li></ul>
      </div>
      <div v-else-if="errorTarget" class="mb-3 p-2 bg-light rounded small">
        <strong>{{ errorTarget.상품명 }}</strong> | {{ errorTarget.바코드 }} | 数量<small>(수량)</small>: {{ errorTarget.구매요청수량 }}
        <span v-if="errorTarget.입고오류상세" class="ml-2">
          <b-badge :variant="errorStatusVariant(errorTarget.입고오류상세.오류상태)">{{ errorStatusText(errorTarget.입고오류상세.오류상태) }}</b-badge>
        </span>
      </div>

      <!-- 일괄 오류 등록 — 옵션(중문·한글 속성)마다 카드 1장, 순서대로 채우고 맨 아래에서 한꺼번에 제출(#127 SYL) -->
      <template v-if="errorMode === 'register' && errorBulk">
        <div class="small text-muted mb-2">批量登记错误(일괄 오류 등록) {{ bulkTargets.length }}件 — 选项逐个填写(옵션을 하나씩 차례로 채우세요) · 图片按选项上传(사진은 옵션별) · 最后一次提交(마지막에 한 번에 제출)</div>
        <div v-for="(t, ti) in bulkTargets" :key="'bc' + t._id" class="border rounded mb-2" :class="bulkCardClass(t)">
          <div class="p-2 d-flex align-items-center" style="cursor:pointer" @click="bulkOpenId = bulkOpenId === t._id ? '' : t._id">
            <b-badge :variant="bulkDone(t) ? 'success' : 'secondary'" class="mr-2">{{ ti + 1 }}</b-badge>
            <div class="flex-grow-1 small">
              <strong>{{ t.상품명 }}</strong> | {{ t.바코드 }} | 数量<small>(수량)</small> {{ t.구매요청수량 }}
              <div v-if="bulkOptionText(t)" style="font-weight:600">{{ bulkOptionText(t) }}</div>
            </div>
            <span class="small" :class="bulkDone(t) ? 'text-success' : 'text-danger'">{{ bulkDone(t) ? '已填(입력됨)' : '未填(미입력)' }}</span>
            <span class="ml-2">{{ bulkOpenId === t._id ? '▲' : '▼' }}</span>
          </div>
          <div v-if="bulkOpenId === t._id && bulkForms[t._id]" class="p-2 border-top">
            <div class="form-group">
              <label>错误类型<small>(오류유형)</small> <span class="text-danger">*</span></label>
              <b-form-select v-model="bulkForms[t._id].오류유형" :options="errorTypes" class="err-type-select" :style="errTypeStyle(bulkForms[t._id].오류유형, errorTypes)"></b-form-select>
            </div>
            <div class="form-group">
              <label>错误数量<small>(오류 수량)</small></label>
              <input type="number" min="1" step="1" class="form-control" v-model.number="bulkForms[t._id].오류수량" :placeholder="'全部(전체) ' + t.구매요청수량">
              <small class="text-danger d-block">套装按组填(세트는 조 단위로 입력)</small>
            </div>
            <div class="form-group">
              <label>错误内容<small>(오류내용)</small></label>
              <textarea class="form-control" v-model="bulkForms[t._id].오류내용" rows="2" placeholder="错误详细内容(오류 상세 내용)"></textarea>
            </div>
            <div class="form-row">
              <div class="form-group col-md-6">
                <label>可出库数量<small>(출고가능수량)</small></label>
                <input type="number" min="0" step="1" class="form-control" v-model.number="bulkForms[t._id].출고가능수량">
              </div>
              <div class="form-group col-md-6">
                <label>不成组良品数量<small>(미구성 양품수량)</small></label>
                <input type="text" maxlength="100" class="form-control" v-model.trim="bulkForms[t._id].미구성양품수량" placeholder="例: 3组 + 散2个">
              </div>
            </div>
            <div class="form-group">
              <label>是否已作业&作业内容<small>(작업여부&작업내용)</small></label>
              <textarea class="form-control" v-model="bulkForms[t._id].작업내용" rows="2" maxlength="500"></textarea>
            </div>
            <div class="form-group">
              <label>图片/视频上传<small>(이 옵션의 이미지·영상)</small></label>
              <div class="d-flex align-items-center mb-2">
                <input type="file" multiple accept="image/*,video/*" @change="onBulkFiles(t, $event)" class="form-control-file">
                <b-spinner v-if="bulkUploading[t._id]" small class="ml-2"></b-spinner>
              </div>
              <div v-if="bulkForms[t._id].이미지URLs.length" class="d-flex flex-wrap" style="gap:6px">
                <div v-for="(url, i) in bulkForms[t._id].이미지URLs" :key="url + i" class="position-relative border rounded" style="width:120px;height:90px;overflow:hidden;background:#f1f3f5">
                  <video v-if="isVideoUrl(url)" :src="url" controls preload="metadata" style="width:100%;height:100%;object-fit:cover"></video>
                  <a v-else :href="url" target="_blank" rel="noopener"><img :src="url" alt="" style="width:100%;height:100%;object-fit:cover"></a>
                  <b-button size="sm" variant="danger" class="position-absolute py-0 px-1" style="top:2px;right:2px;line-height:1.2" title="删除(삭제)" @click="removeBulkUrl(t, url)">×</b-button>
                </div>
              </div>
            </div>
            <div class="d-flex">
              <b-button v-if="ti > 0" size="sm" variant="outline-secondary" @click="copyPrevBulk(ti)">复制上一件内容<small>(앞 옵션 내용 복사)</small></b-button>
              <b-button v-if="ti < bulkTargets.length - 1" size="sm" variant="primary" class="ml-auto" @click="bulkOpenId = bulkTargets[ti + 1]._id">下一个选项 ▶<small>(다음 옵션)</small></b-button>
            </div>
          </div>
        </div>
      </template>

      <!-- 오류 등록 모드 -->
      <template v-if="errorMode === 'register' && !errorBulk">
        <div class="form-group">
          <label>错误类型<small>(오류유형)</small> <span class="text-danger">*</span></label>
          <b-form-select v-model="errorForm.오류유형" :options="errorTypes" class="err-type-select" :style="errTypeStyle(errorForm.오류유형, errorTypes)"></b-form-select>
        </div>
        <div class="form-row" v-if="!errorBulk">
          <div class="form-group col-12">
            <label>错误数量<small>(오류 수량)</small></label>
            <input type="number" min="1" step="1" class="form-control" v-model.number="errorForm.오류수량"
                   :placeholder="'全部(전체) ' + (errorTarget ? errorTarget.구매요청수량 : '')">
            <small class="text-danger d-block">套装按组填(세트는 조 단위로 입력)</small>
            <small class="text-muted">少于采购数量时只把该数量登记为错误，其余可继续入库(구매수량보다 적으면 그 수량만 오류로, 나머지는 계속 입고 가능)</small>
          </div>
        </div>
        <div class="form-group">
          <label>错误内容<small>(오류내용)</small></label>
          <textarea class="form-control" v-model="errorForm.오류내용" rows="3" placeholder="错误详细内容(오류 상세 내용)"></textarea>
        </div>
        <div class="form-row">
          <div class="form-group col-md-6">
            <label>可出库数量<small>(출고가능수량)</small></label>
            <input type="number" min="0" step="1" class="form-control" v-model.number="errorForm.출고가능수량">
          </div>
          <div class="form-group col-md-6">
            <label>不成组良品数量<small>(미구성 양품수량)</small></label>
            <input type="text" maxlength="100" class="form-control" v-model.trim="errorForm.미구성양품수량" placeholder="例: 3组 + 散2个 (글자+숫자 가능)">
          </div>
        </div>
        <div class="form-group">
          <label>是否已作业&作业内容<small>(작업여부&작업내용)</small></label>
          <textarea class="form-control" v-model="errorForm.작업내용" rows="2" maxlength="500"></textarea>
        </div>
        <div class="form-group">
          <label>图片/视频上传<small>(이미지·영상 업로드)</small></label>
          <div class="d-flex align-items-center mb-2">
            <!-- 이미지·영상 여러 개를 한 번에 고르면 바로 올라가고 아래에 미리보기가 뜬다(2026-10-16 SYL #45) -->
            <input type="file" ref="errorFileInput" multiple accept="image/*,video/*" @change="onErrorFilesSelected" class="form-control-file">
            <b-spinner v-if="uploadingImages" small class="ml-2"></b-spinner>
            <small v-if="uploadingImages" class="ml-1 text-muted">上传中...(업로드 중)</small>
          </div>
          <div v-if="errorForm.이미지URLs.length" class="small text-success mb-1">
            ✅ 上传完成<small>(업로드 완료)</small>: {{ errorForm.이미지URLs.length }}个
          </div>
          <div v-if="errorForm.이미지URLs.length" class="d-flex flex-wrap" style="gap:6px">
            <div v-for="(url, i) in errorForm.이미지URLs" :key="url + i" class="position-relative border rounded" style="width:120px;height:90px;overflow:hidden;background:#f1f3f5">
              <video v-if="isVideoUrl(url)" :src="url" controls preload="metadata" style="width:100%;height:100%;object-fit:cover"></video>
              <a v-else :href="url" target="_blank" rel="noopener"><img :src="url" alt="" style="width:100%;height:100%;object-fit:cover"></a>
              <b-button size="sm" variant="danger" class="position-absolute py-0 px-1" style="top:2px;right:2px;line-height:1.2" title="删除(삭제)" @click="removeErrorUrl(url)">×</b-button>
            </div>
          </div>
          <import-photo-panel v-if="errorTarget" :item="errorTarget" kind="오류사진" class="mt-2" @error-photos="onErrorPhotos" @photo-removed="onErrorPhotoRemoved"></import-photo-panel>
        </div>
      </template>

      <!-- 오류 처리 모드 (이미 등록된 오류 건) -->
      <template v-if="errorMode === 'resolve'">
        <div class="mb-3 p-2 border rounded small">
          <strong>错误类型<small>(오류유형)</small>:</strong> {{ errorTarget.입고오류상세.오류유형 }}<br>
          <strong>错误内容<small>(오류내용)</small>:</strong> {{ errorTarget.입고오류상세.오류내용 || '-' }}<br>
          <div v-if="errorTarget.입고오류상세.이미지 && errorTarget.입고오류상세.이미지.length">
            <strong>图片<small>(이미지)</small>:</strong>
            <!-- 등록 때와 같은 썸네일 — 글자 링크(图片1·2…)로는 어느 사진인지 열어 봐야 알았다(옛 PR #1307) -->
            <div class="d-flex flex-wrap mt-1" style="gap:6px">
              <div v-for="(img, i) in errorTarget.입고오류상세.이미지" :key="img + i" class="border rounded" style="width:120px;height:90px;overflow:hidden;background:#f1f3f5">
                <video v-if="isVideoUrl(img)" :src="img" controls preload="metadata" style="width:100%;height:100%;object-fit:cover"></video>
                <a v-else :href="img" target="_blank" rel="noopener"><img :src="img" :alt="'图片' + (i+1)" style="width:100%;height:100%;object-fit:cover"></a>
              </div>
            </div>
          </div>
        </div>
        <!-- 1688 报价 vs 实付(웹훅) — 낮아지면 기존 청구서 유지, 높아지면 재청구(YZY 10-06) -->
        <div v-if="adjustInfo && adjustInfo.가격변동" class="mb-2 small" :class="adjustInfo.가격변동.판정 === '재청구필요' ? 'text-danger' : 'text-muted'">
          1688 报价<small>(견적)</small> {{ adjustInfo.가격변동.견적_CNY }} / 实付<small>(실결제)</small> {{ adjustInfo.가격변동.실결제_CNY }} CNY —
          {{ priceVarianceText(adjustInfo.가격변동.판정) }}
        </div>
        <!-- 이미 조정한 건 — 고른 처리와 되돌리기 -->
        <div v-if="adjustInfo && adjustInfo.조정" class="p-2 border rounded small mb-2">
          <div><strong>已调整<small>(조정됨)</small>:</strong> {{ adjustInfo.조정.라벨 }}</div>
          <div v-if="adjustInfo.조정.요약" class="text-muted">{{ adjustInfo.조정.요약 }}</div>
          <div class="text-muted">{{ new Date(adjustInfo.조정.일시).toLocaleString() }} | {{ adjustInfo.조정.처리자 }}</div>
          <b-button size="sm" variant="outline-danger" class="mt-1" @click="undoAdjust" :disabled="loading">撤销调整<small>(되돌리기 — 다시 선택)</small></b-button>
        </div>
        <template v-else>
          <!-- 입고 조정 선택지 — 고르면 수량(재고)·금액(견적)·원가가 같이 반영된다. 굵은 것 = 기본(가장 흔한 처리) -->
          <div v-if="adjustChoices.length" class="form-group">
            <label>处理选择<small>(처리 선택 — 수량·금액·원가 같이 반영)</small></label>
            <b-form-radio-group v-model="resolveForm.선택" stacked>
              <b-form-radio v-for="c in adjustChoices" :key="c.key" :value="c.key">
                <span :class="{ 'font-weight-bold': c.기본 }">{{ c.라벨 }}</span>
              </b-form-radio>
              <b-form-radio value=""><span class="text-muted">只改状态(상태만 변경 — 기존 방식)</span></b-form-radio>
            </b-form-radio-group>
          </div>
          <div v-if="selectedChoice && (selectedChoice.환불 || selectedChoice.추가)" class="form-row">
            <div class="form-group col-6">
              <label>{{ selectedChoice.환불 ? '1688退款' : '追加付款' }}<small>(CNY)</small><span v-if="!selectedChoice.환불선택" class="text-danger"> *</span></label>
              <input v-if="selectedChoice.환불" type="number" min="0" step="0.01" class="form-control form-control-sm" v-model="resolveForm.환불_CNY">
              <input v-else type="number" min="0" step="0.01" class="form-control form-control-sm" v-model="resolveForm.추가_CNY">
              <small v-if="adjustInfo && adjustInfo.이건" class="text-muted">本条报价<small>(이 건 견적)</small> {{ adjustInfo.이건.견적_CNY }} CNY</small>
            </div>
            <div v-if="selectedChoice.환불" class="form-group col-6 pt-4">
              <b-form-checkbox v-model="resolveForm.화주환불">退给客户(扣减报价)<small>(화주 환불 · 견적 차감)</small></b-form-checkbox>
            </div>
          </div>
          <!-- 옵션 뒤바뀜 — 같은 1688 주문에서 상대 상품을 고른다(많이 온 쪽이 기본 체크) -->
          <div v-if="selectedChoice && (selectedChoice.재배분 || selectedChoice.초과반품)" class="form-group">
            <label class="small font-weight-bold">同一1688订单的对应商品<small>(같은 1688 주문의 상대 상품)</small></label>
            <div v-if="!adjustInfo || !adjustInfo.형제.length" class="small text-muted">无(없음)</div>
            <div v-else class="small">
              <b-form-checkbox v-for="sib in adjustInfo.형제" :key="sib._id" v-model="resolveForm.대상ids" :value="sib._id">
                {{ sib.옵션 || sib.바코드 }} — 采购<small>(요청)</small> {{ sib.구매요청수량 }} / 入库<small>(입고)</small> {{ sib.입고수량 }}
              </b-form-checkbox>
            </div>
          </div>
          <div v-if="!resolveForm.선택" class="form-group">
            <label>处理方式<small>(처리방법)</small> <span class="text-danger">*</span></label>
            <div>
              <b-form-radio-group v-model="resolveForm.처리방법" :options="resolveMethods" buttons button-variant="outline-primary" size="sm"></b-form-radio-group>
            </div>
          </div>
        </template>
        <div v-if="!(adjustInfo && adjustInfo.조정) && (effectiveMethod === '반품' || effectiveMethod === '교환')" class="mt-2 p-2 border rounded">
          <label class="font-weight-bold">退货信息<small>(반품정보)</small></label>
          <input type="text" class="form-control form-control-sm mb-1" v-model="resolveForm.반품정보.수취인" placeholder="退货收件人(반품수취인)">
          <input type="text" class="form-control form-control-sm mb-1" v-model="resolveForm.반품정보.연락처" placeholder="退货联系方式(반품연락처)">
          <textarea class="form-control form-control-sm mb-1" v-model="resolveForm.반품정보.주소" rows="2" placeholder="退货地址(반품주소)"></textarea>
          <input type="text" class="form-control form-control-sm" v-model="resolveForm.반품정보.운송장번호" placeholder="退货快递单号(반품운송장번호)">
        </div>
        <div v-if="!resolveForm.선택 && resolveForm.처리방법 === '미도착종결'" class="mt-2 p-2 border rounded small text-muted">
          不再等待剩余数量，直接结单 — 库存 0、不出库、不计入物流费用。1688采购金额不变。<br>(남은 수량을 더 기다리지 않고 종결 — 재고 0·출고 안 함·물류비 제외. 1688 구매금액은 그대로)
        </div>
        <div class="form-group mt-2">
          <label>处理备注<small>(처리 메모)</small> <span v-if="!resolveForm.선택 && resolveForm.처리방법 === '미도착종결'" class="text-danger">* 结单原因(사유)</span></label>
          <textarea class="form-control" v-model="resolveForm.메모" rows="2" placeholder="处理相关备注(처리 관련 메모, 선택)"></textarea>
        </div>
        <!-- 처리이력 표시 -->
        <div v-if="errorTarget.입고오류상세.처리이력 && errorTarget.입고오류상세.처리이력.length" class="mt-3">
          <label class="font-weight-bold small">处理记录<small>(처리이력)</small></label>
          <div class="small text-muted" v-for="(h, i) in errorTarget.입고오류상세.처리이력" :key="i">
            <b-badge variant="secondary" class="mr-1">{{ h.상태 === '처리기록' ? '处理记录' : errorStatusText(h.상태) }}</b-badge>
            {{ new Date(h.처리일).toLocaleString() }} | {{ h.처리자 }} | {{ h.메모 }}
          </div>
        </div>
      </template>

      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">取消<small>(취소)</small></b-button>
        <b-button v-if="errorMode === 'register'" variant="danger" @click="errorBulk ? submitBulkError() : submitError()" :disabled="loading || (!errorBulk && !errorForm.오류유형) || (errorBulk && Object.values(bulkUploading).some(Boolean))">
          登记错误<small>(오류 등록)</small>
        </b-button>
        <b-button v-if="errorMode === 'resolve' && !(adjustInfo && adjustInfo.조정)" variant="primary" @click="submitResolve" :disabled="loading || !(resolveForm.선택 || resolveForm.처리방법)">
          确认处理<small>(처리 확정)</small>
        </b-button>
      </template>
    </b-modal>

    <!-- ==================== 수동출력 모달 ==================== -->
    <!-- 상품을 검색·선택하지 않고 값을 직접 넣어 출력한다. 인쇄 영역은 아래 기존 것을 그대로 쓴다
         (条码 = #printAreaCustom, 箱号 = #boxLabelPrintAreaManual — 박스배정 라벨과 같은 모양) -->
    <b-modal v-model="showManualPrint" title="🖨️ 手动打印(수동출력)" hide-footer size="md">
      <b-tabs v-model="manualTab" small>
        <b-tab title="条码(바코드)">
          <div class="mt-2">
            <label class="small mb-0">条码值(바코드 값)</label>
            <input type="text" class="form-control form-control-sm mb-2" v-model="manualBarcode" placeholder="例: 8809xxxxxxxxx">
            <label class="small mb-0">SKU<small class="text-muted">(选填/선택)</small></label>
            <input type="text" class="form-control form-control-sm mb-2" v-model="manualSku">
            <label class="small mb-0">显示内容(표시 내용: 商品名/选项 등, 可多行)</label>
            <textarea class="form-control form-control-sm mb-2" rows="3" v-model="customPrintContent" placeholder="商品名 / 选项"></textarea>
            <div class="d-flex align-items-center flex-wrap mb-2" style="gap:8px;font-size:0.95rem">
              <b-form-checkbox v-model="printOpts.sku" switch>SKU</b-form-checkbox>
              <b-form-checkbox v-model="printOpts.kc" switch>KC</b-form-checkbox>
              <b-form-checkbox v-model="printOpts.food" switch>食品用</b-form-checkbox>
            </div>
            <div class="d-flex align-items-center" style="gap:4px;font-size:0.9rem">
              <span>数量</span><input type="number" class="form-control form-control-sm" style="width:90px" v-model.number="printQty" min="1">
              <span>间距</span><input type="number" class="form-control form-control-sm" style="width:50px" v-model.number="printGap" min="0"><span>px</span>
              <span>字号</span><input type="number" class="form-control form-control-sm" style="width:50px" v-model.number="printFontSize" min="50" max="150"><span>%</span>
              <b-button size="sm" variant="primary" class="ml-auto" @click="printManualBarcode">🖨️ 打印</b-button>
            </div>
          </div>
        </b-tab>
        <b-tab title="箱号(박스번호)">
          <div class="mt-2">
            <label class="small mb-0">箱号(박스번호) <small class="text-muted">— 换行=标签上分两行(줄바꿈하면 라벨에 두 줄로)</small></label>
            <textarea class="form-control form-control-sm mb-2" rows="2" v-model="manualBoxNo" placeholder="Z-woonjung&#10;FK0922003"></textarea>
            <div class="d-flex align-items-center" style="gap:4px;font-size:0.9rem">
              <b-form-checkbox v-model="manualBoxSeq" switch>连续编号(연속번호)</b-form-checkbox>
              <template v-if="manualBoxSeq">
                <span class="ml-2">起始号(시작번호)</span><input type="number" class="form-control form-control-sm" style="width:70px" v-model.number="manualBoxStart" min="0">
              </template>
            </div>
            <div class="d-flex align-items-center mt-2" style="gap:4px;font-size:0.9rem">
              <span>数量</span><input type="number" class="form-control form-control-sm" style="width:90px" v-model.number="manualBoxQty" min="1">
              <b-button size="sm" variant="primary" class="ml-auto" @click="printManualBox">🖨️ 打印</b-button>
            </div>
            <div v-if="manualBoxSeq && manualBoxNo.trim()" class="small text-muted mt-1">
              {{ manualBoxSeqPreview }}
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </b-modal>

    <!-- 수동 박스번호 라벨 — BoxAssignModal 의 #boxLabelPrintArea 와 같은 모양 -->
    <div id="boxLabelPrintAreaManual" style="display:none">
      <!-- 박스 라벨지 = 가로 80mm × 세로 60mm. 내용을 그 한가운데에 둔다 -->
      <div v-for="(lb, li) in manualBoxList" :key="'mb-' + li" style="page-break-after:always;width:100vw;height:100vh;box-sizing:border-box;padding:2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;overflow:hidden">
        <!-- 글자는 바코드 자리를 뺀 나머지에 맞춰 크기를 줄인다(긴 업체명이 바코드를 밀어내지 않게) -->
        <div :style="{ fontSize: boxLabelFontSize(lb) + 'px' }" style="flex:1 1 auto;min-height:0;width:100%;display:flex;align-items:center;justify-content:center;line-height:1.1;font-weight:bold;white-space:pre-line;overflow-wrap:anywhere;overflow:hidden;margin-bottom:2mm">{{ lb }}</div>
        <!-- 바코드 자리 18mm 고정 -->
        <div style="flex:0 0 18mm;width:100%;line-height:0;text-align:center"><barcode :value="lb.replace(/\n/g, '-')" format="CODE128B" :height="60" :width="1" :margin="4" :display-value="false"></barcode></div>
      </div>
    </div>

    <!-- ==================== 숨겨진 프린트 영역 ==================== -->
    <div class="d-none" id="printArea">
      <div v-for="n in (printReady ? printQty : 0)" :key="n" :style="'break-inside:avoid;page-break-inside:avoid;' + (n < printQty ? 'margin-bottom:' + printGap + 'px;break-after:page;page-break-after:always' : '')">
        <!-- 위생검역(卫生检疫) 라벨 — 제목 + 값이 있는 항목 줄만(2026-10-16 YZY) -->
        <div v-if="printData.hyg" style="width:300px;text-align:center;display:flex;flex-direction:column;align-items:center">
          <div v-if="printOpts.barcode" style="display:flex;flex-direction:column;align-items:center;width:100%;margin-top:0;margin-bottom:0">
            <barcode :value="printData.barcode || '0'" tag="svg" height="50" :options="{ lineColor: '#0275d8', text: 'Scan', margin: 0 }"></barcode>
          </div>
          <div :style="'width:100%;text-align:left;font-size:' + printFontSize + '%'">
            <div style="font-size:90%;font-weight:bold">위생용품 관리법에 의한 품질 표시</div>
            <template v-for="k in HYG_PRINT_KEYS">
              <span v-if="printData.label && printData.label[k]" :key="'hp' + k" style="font-size:80%;color:black;line-height:130%;display:block;white-space:pre-line">· {{ k }}: {{ printData.label[k] }}</span>
            </template>
          </div>
        </div>
        <div v-else style="width:300px;text-align:center;display:flex;flex-direction:column;align-items:center">
          <div style="display:flex;justify-content:space-between;width:90%;margin-bottom:0;line-height:1.2">
            <p style="font-size:90%;color:black;margin-bottom:0">Made in China</p>
            <p v-if="printOpts.sku && printData.sku" style="font-size:90%;color:black;margin-bottom:0">SKU: {{ printData.sku }}</p>
          </div>
          <div v-if="printOpts.barcode" style="display:flex;flex-direction:column;align-items:center;width:100%;margin-top:0;margin-bottom:0">
            <barcode :value="printData.barcode || '0'" tag="svg" height="50" :options="{ lineColor: '#0275d8', text: 'Scan', margin: 0 }"></barcode>
          </div>
          <!-- 로고는 오른쪽에 float — 전용 줄 없이 왼쪽 공간으로 아래 글(상품명·한글표시사항)이 흘러 들어온다. 로고 없으면 display:contents 로 기존 가운데 정렬 유지 -->
          <div :style="(printOpts.barcode && !printOpts.label && (printOpts.kc || printOpts.food)) ? 'width:100%' : 'display:contents'">
            <div v-if="printOpts.barcode && !printOpts.label && (printOpts.kc || printOpts.food)" style="float:right;display:flex;align-items:center;height:56px;margin:0 10px 0 4px">
              <img v-if="printOpts.food" style="height:100%" :src="require('../../../assets/images/small/ForFood.png')">
              <img v-if="printOpts.kc" style="height:100%;margin-left:5px" :src="require('../../../assets/images/small/KcMark.jpg')">
            </div>
          <div v-if="printOpts.food" :style="'width:100%;font-size:' + printFontSize + '%'">
            <span v-if="!printOpts.label" style="font-size:80%;color:black;display:block">{{ printData.name }}</span>
            <div style="font-size:90%;font-weight:bold">식품위생법에 의한 한글표시사항</div>
          </div>
          <div :style="'margin:0 10px;text-align:left;line-height:1.3;font-size:' + printFontSize + '%'">
            <span v-if="!printOpts.food && !printOpts.label" style="font-size:80%;color:black;line-height:1.3;display:block">{{ printData.name }}</span>
            <template v-if="printOpts.label && printData.label">
              <span style="font-size:80%;color:black;line-height:130%;display:block">· 상품명: {{ printData.name }}</span>
              <span v-if="labelRowShown('수입원/판매원')" style="font-size:80%;color:black;line-height:130%;display:block">· 수입원/판매원: {{ printData.label['수입원/판매원'] }}</span>
              <div style="display:flex;justify-content:space-between;">
                <div>
                  <span v-if="labelRowShown('제조원')" style="font-size:80%;color:black;line-height:130%;display:block">· 제조원: {{ printData.label.제조원 }}</span>
                  <span v-if="labelRowShown('제조국')" style="font-size:80%;color:black;line-height:130%;display:block">· 제조국: {{ printData.label.제조국 }}</span>
                  <span v-if="labelRowShown('내용량')" style="font-size:80%;color:black;line-height:130%;display:block">· 내용량: {{ printData.label.내용량 }}</span>
                </div>
                <div v-if="printOpts.food" style="height:58px;margin-right:10px;margin-left:10px;">
                  <img style="height:100%" :src="require('../../../assets/images/small/ForFood.png')">
                </div>
              </div>
              <span v-if="labelRowShown('원료명및성분명(재질)')" style="font-size:80%;color:black;line-height:130%;display:block">· 원료명및성분명(재질): {{ printData.label['원료명및성분명(재질)'] }}</span>
              <template v-for="k in LABEL_EXTRA_KEYS">
                <span v-if="printData.label[k]" :key="'lx' + k" style="font-size:80%;color:black;line-height:130%;display:block">· {{ k }}: {{ printData.label[k] }}</span>
              </template>
              <!-- 韩文标签资料 「기타」 — 한 줄에 「라벨: 값」 하나(붙여넣기 인식에서 사전에 없던 라벨) -->
              <template v-if="printData.master && printData.label.기타">
                <span v-for="(ln, i) in String(printData.label.기타).split('\n').filter(x => x.trim())" :key="'lo' + i" style="font-size:80%;color:black;line-height:130%;display:block">· {{ ln.trim() }}</span>
              </template>
              <div style="display:flex;justify-content:space-between;">
                <div>
                  <span v-if="labelRowShown('상품유형')" style="font-size:80%;color:black;line-height:130%;display:block">· 상품유형: {{ printData.label.상품유형 }}</span>
                  <span v-if="labelRowShown('사용시주의사항')" style="font-size:80%;color:black;line-height:130%;display:block">· 사용시주의사항: {{ printData.label.사용시주의사항 }}</span>
                  <span v-if="labelRowShown('사용기준')" style="font-size:80%;color:black;line-height:130%;display:block">· 사용기준: {{ printData.label.사용기준 }}</span>
                </div>
                <div v-if="printOpts.kc" style="height:60px;margin-right:10px;margin-top:10px;margin-left:10px;">
                  <img style="height:100%" :src="require('../../../assets/images/small/KcMark.jpg')">
                </div>
              </div>
            </template>
            <div v-if="!printOpts.barcode && !printOpts.label && (printOpts.kc || printOpts.food)" style="display:flex;justify-content:flex-end;height:56px;margin-top:5px">
              <img v-if="printOpts.food" style="height:100%" :src="require('../../../assets/images/small/ForFood.png')">
              <img v-if="printOpts.kc" style="height:100%;margin-left:5px" :src="require('../../../assets/images/small/KcMark.jpg')">
            </div>
          </div>
            <div style="clear:both"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 수정 출력 프린트 영역 -->
    <div class="d-none" id="printAreaCustom">
      <div v-for="n in (printReady ? printQty : 0)" :key="n" :style="'break-inside:avoid;page-break-inside:avoid;' + (n < printQty ? 'margin-bottom:' + printGap + 'px;break-after:page;page-break-after:always' : '')">
        <div style="width:300px;text-align:center;display:flex;flex-direction:column;align-items:center">
          <div style="display:flex;justify-content:space-between;width:90%;margin-bottom:0;line-height:1.2">
            <p style="font-size:90%;color:black;margin-bottom:0">Made in China</p>
            <p v-if="printOpts.sku && printData.sku" style="font-size:90%;color:black;margin-bottom:0">SKU: {{ printData.sku }}</p>
          </div>
          <div v-if="printOpts.barcode" style="display:flex;flex-direction:column;align-items:center;width:100%;margin-top:0;margin-bottom:0">
            <barcode :value="printData.barcode || '0'" tag="svg" height="50" :options="{ lineColor: '#0275d8', text: 'Scan', margin: 0 }"></barcode>
          </div>
          <!-- 로고는 오른쪽에 float — 전용 줄 없이 왼쪽 공간으로 아래 글(상품명·한글표시사항)이 흘러 들어온다. 로고 없으면 display:contents 로 기존 가운데 정렬 유지 -->
          <div :style="(printOpts.barcode && (printOpts.kc || printOpts.food)) ? 'width:100%' : 'display:contents'">
            <!-- 표시사항 항목이 있으면 로고는 「식품위생법에 의한 한글표시사항」 제목 아래에서 시작한다(제목은 전체 폭). 항목이 없으면 로고만 남는 줄이 생기므로 상품명 옆(기존) -->
            <div v-if="printOpts.barcode && (printOpts.kc || printOpts.food) && !(printOpts.food && customPrintBodyLines.length)" style="float:right;display:flex;align-items:center;height:56px;margin:0 10px 0 4px">
              <img v-if="printOpts.food" style="height:100%" :src="require('../../../assets/images/small/ForFood.png')">
              <img v-if="printOpts.kc" style="height:100%;margin-left:5px" :src="require('../../../assets/images/small/KcMark.jpg')">
            </div>
          <div v-if="printOpts.food" :style="'width:100%;font-size:' + printFontSize + '%'">
            <span v-if="customPrintLines.length" style="font-size:80%;color:black;display:block">{{ customPrintLines[0] }}</span>
            <div style="font-size:90%;font-weight:bold">식품위생법에 의한 한글표시사항</div>
          </div>
            <div v-if="printOpts.barcode && printOpts.food && customPrintBodyLines.length" :style="'float:right;display:flex;align-items:center;height:56px;margin:' + labelLogoDropEm + 'em 10px 0 4px'">
              <img style="height:100%" :src="require('../../../assets/images/small/ForFood.png')">
              <img v-if="printOpts.kc" style="height:100%;margin-left:5px" :src="require('../../../assets/images/small/KcMark.jpg')">
            </div>
          <div :style="'margin:0 10px;text-align:left;line-height:1.3;font-size:' + printFontSize + '%'" style="white-space:pre-line">
            <span v-for="(line, li) in customPrintBodyLines" :key="li" style="font-size:80%;color:black;line-height:130%;display:block">{{ line }}</span>
          </div>
            <div style="clear:both"></div>
          </div>
          <div v-if="!printOpts.barcode && (printOpts.kc || printOpts.food)" style="display:flex;justify-content:flex-end;height:56px;margin-top:5px">
            <img v-if="printOpts.food" style="height:100%" :src="require('../../../assets/images/small/ForFood.png')">
            <img v-if="printOpts.kc" style="height:100%;margin-left:5px" :src="require('../../../assets/images/small/KcMark.jpg')">
          </div>
        </div>
      </div>
    </div>

    <!-- 전체 바코드 일괄 출력 프린트 영역 -->
    <div class="d-none" id="printAreaAll">
      <div v-for="(pd, pi) in printAllData" :key="pi" :style="'break-inside:avoid;page-break-inside:avoid;' + (pi < printAllData.length - 1 ? 'margin-bottom:' + printGap + 'px;break-after:page;page-break-after:always' : '')">
        <div style="width:300px;text-align:center;display:flex;flex-direction:column;align-items:center">
          <div style="display:flex;justify-content:space-between;width:90%;margin-bottom:0;line-height:1.2">
            <p style="font-size:90%;color:black;margin-bottom:0">Made in China</p>
            <p v-if="printOpts.sku && pd.sku" style="font-size:90%;color:black;margin-bottom:0">SKU: {{ pd.sku }}</p>
          </div>
          <div v-if="printOpts.barcode" style="display:flex;flex-direction:column;align-items:center;width:100%;margin-top:0;margin-bottom:0">
            <barcode :value="pd.barcode || '0'" tag="svg" height="50" :options="{ lineColor: '#0275d8', text: 'Scan', margin: 0 }"></barcode>
          </div>
          <!-- 로고는 오른쪽에 float — 전용 줄 없이 왼쪽 공간으로 아래 글(상품명·한글표시사항)이 흘러 들어온다. 로고 없으면 display:contents 로 기존 가운데 정렬 유지 -->
          <div :style="(printOpts.barcode && !printOpts.label && (printOpts.kc || printOpts.food)) ? 'width:100%' : 'display:contents'">
            <div v-if="printOpts.barcode && !printOpts.label && (printOpts.kc || printOpts.food)" style="float:right;display:flex;align-items:center;height:56px;margin:0 10px 0 4px">
              <img v-if="printOpts.food" style="height:100%" :src="require('../../../assets/images/small/ForFood.png')">
              <img v-if="printOpts.kc" style="height:100%;margin-left:5px" :src="require('../../../assets/images/small/KcMark.jpg')">
            </div>
          <div v-if="printOpts.food" :style="'width:100%;font-size:' + printFontSize + '%'">
            <span v-if="!printOpts.label" style="font-size:80%;color:black;display:block">{{ pd.name }}</span>
            <div style="font-size:90%;font-weight:bold">식품위생법에 의한 한글표시사항</div>
          </div>
          <div :style="'margin:0 10px;text-align:left;line-height:1.3;font-size:' + printFontSize + '%'">
            <span v-if="!printOpts.food && !printOpts.label" style="font-size:80%;color:black;line-height:1.3;display:block">{{ pd.name }}</span>
            <template v-if="printOpts.label && pd.label">
              <span style="font-size:80%;color:black;line-height:130%;display:block">· 상품명: {{ pd.name }}</span>
              <span style="font-size:80%;color:black;line-height:130%;display:block">· 수입원/판매원: {{ pd.label['수입원/판매원'] }}</span>
              <div style="display:flex;justify-content:space-between;">
                <div>
                  <span style="font-size:80%;color:black;line-height:130%;display:block">· 제조원: {{ pd.label.제조원 }}</span>
                  <span style="font-size:80%;color:black;line-height:130%;display:block">· 제조국: {{ pd.label.제조국 }}</span>
                  <span style="font-size:80%;color:black;line-height:130%;display:block">· 내용량: {{ pd.label.내용량 }}</span>
                </div>
                <div v-if="printOpts.food" style="height:58px;margin-right:10px;margin-left:10px;">
                  <img style="height:100%" :src="require('../../../assets/images/small/ForFood.png')">
                </div>
              </div>
              <span style="font-size:80%;color:black;line-height:130%;display:block">· 원료명및성분명(재질): {{ pd.label['원료명및성분명(재질)'] }}</span>
              <div style="display:flex;justify-content:space-between;">
                <div>
                  <span style="font-size:80%;color:black;line-height:130%;display:block">· 상품유형: {{ pd.label.상품유형 }}</span>
                  <span style="font-size:80%;color:black;line-height:130%;display:block">· 사용시주의사항: {{ pd.label.사용시주의사항 }}</span>
                  <span style="font-size:80%;color:black;line-height:130%;display:block">· 사용기준: {{ pd.label.사용기준 }}</span>
                </div>
                <div v-if="printOpts.kc" style="height:60px;margin-right:10px;margin-top:10px;margin-left:10px;">
                  <img style="height:100%" :src="require('../../../assets/images/small/KcMark.jpg')">
                </div>
              </div>
            </template>
            <div v-if="!printOpts.barcode && !printOpts.label && (printOpts.kc || printOpts.food)" style="display:flex;justify-content:flex-end;height:56px;margin-top:5px">
              <img v-if="printOpts.food" style="height:100%" :src="require('../../../assets/images/small/ForFood.png')">
              <img v-if="printOpts.kc" style="height:100%;margin-left:5px" :src="require('../../../assets/images/small/KcMark.jpg')">
            </div>
          </div>
            <div style="clear:both"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { coupassMenuTitle } from './coupassMenu';
import axios from 'axios';
import VueBarcode from 'vue-barcode';
import AWS from 'aws-sdk';
import BoxAssignModal from './BoxAssignModal.vue';
import ImportPhotoPanel from './ImportPhotoPanel.vue';
import ConfirmedImportList from './ConfirmedImportList.vue';
import ImportPhotoQueueList from './ImportPhotoQueueList.vue';
import { boxLabelFontSize } from '@/utils/boxLabelFit';
import { processStatusText, errorStatusText } from '@/utils/coupassStatusText';

// 내용수정 출력: 표시사항 옆 食品用/KC 로고를 제목 아래에서 이만큼(표시사항 줄 수) 더 내린다.
// 줄 하나 = 항목 글자(80%) × 줄높이(130%) — 위치 조정은 이 숫자만 바꾸면 된다.
// 진행 순서 — server.js WEBHOOK_STATUS_ORDER 와 같다(logispurchaselist 진행순서와 동일 목록).
// 입고작업 다음(출고확인대기~)이면 이미 창고에 입고된 건이다. 입고대기는 오류 처리 뒤 재입고를 기다리는 건이라 입고 전으로 본다.
const STATUS_ORDER = ['구매전', '협상중', '구매확인', '구매보류', '구매취소', '입금요청', '입금완료', '구매완료',
  '배송중', '배송완료', '입고작업', '출고확인대기', '재고대기', '출고요청', '출고대기', '출고완료', '중국재고', '입고오류',
  '반품확인', '폐기확인', '입고대기', '반품완료', '폐기완료', '미도착종결', '해외입고중', '국내창고입고중',
  '국내창고입고완료', '국내입고중', '쿠팡센터입고완료'];
const INBOUND_DONE_FROM = STATUS_ORDER.indexOf('출고확인대기');

const LABEL_LOGO_DROP_LINES = 1.5;
// 韩文标签资料(식검/KC) 에만 있는 항목 — 값이 있을 때만 라벨에 한 줄씩 더 찍는다(구매요청 한글표시사항에는 없는 키)
const LABEL_EXTRA_KEYS = ['모델명', 'KC인증번호', '제조연월', '소비기한', '보관방법', '반품교환업무 소재지'];

// 入库处理 「要求事项」 칩 — 표시명·순서만 정한다(저장 필드는 그대로, 2026-10-04 YZY).
// 条码·韩文标签 은 같은 필드(barcode)라 칩 하나로 합쳐 보인다. fields 중 하나라도 켜져 있으면 켜짐.
const REQ_CHIPS = [
  { key: 'default', text: '基本检查', fields: ['default'], basic: true },
  { key: 'barcode', text: '条码(韩文标签)', fields: ['barcode'], basic: true },
  { key: 'sticker', text: '贴原产地', fields: ['sticker'] },
  { key: 'sewing', text: '缝标', fields: ['sewing'] },
  { key: 'stamp', text: '盖章', fields: ['stamp'] },
  { key: 'tag', text: '吊牌', fields: ['tag'] },
  { key: 'opp', text: 'OPP(透明袋)', fields: ['opp'] },
  { key: 'LDPE', text: 'LDPE(黑袋)', fields: ['LDPE'] },
  { key: 'masking', text: '盖住中文', fields: ['masking'] },
  { key: 'airpacking', text: '气泡膜', fields: ['airpacking'] }
];

export default {
  name: 'LogisAddImportV0',
  components: { 'barcode': VueBarcode, BoxAssignModal, ImportPhotoPanel, ConfirmedImportList, ImportPhotoQueueList },
  data() {
    return {
      // 위생검역 라벨 출력 항목 순서(2026-10-16) — 서버 LABEL_HYG_KEYS 와 같은 목록
      showErrEdit: false, errEditTarget: null, errEditForm: { 오류내용: '', 오류수량: '' }, errEditing: false, // 입고오류 수정(2026-10-16 #33)
      HYG_PRINT_KEYS: ['제품명', '내용량', '재질', '제조국명', '제조자명', '제조년월일', '수입자명', '위생용품유형', '소비자상담실', '반품 및 교환', '사용시주의사항', '사용기준', '기타'],
      pageTitle: coupassMenuTitle('logis.logisaddimportV0'),
      mainTab: 'import',      // 'import' | 'confirmed'(已跟客户确认)
      confirmedNew: 0,        // 已跟客户确认 새 건 수(내가 처리한 건 중 마지막으로 본 뒤 확인 완료된 것)
      photoQueueActive: null, // 하단 촬영 대기열에서 눌러 연 줄 — 그 주문 건을 다 처리하면 자동으로 done
      // 입고처리에서 1688주문번호로 찾는 일이 더 잦다는 요청(YZY)에 따라 기본값을 바꿨다.
      // 운송장 스캔으로 시작하시는 분은 선택 상자를 快递单号 로 한 번 바꿔야 한다.
      searchType: 'order1688',
      searchKeyword: '',
      searchTypeOptions: [
        { value: 'tracking', text: '快递单号(운송장번호)' },
        { value: 'order1688', text: '1688订单号(1688주문번호)' },
        { value: 'order', text: '货主订单号(화주주문번호)' }
      ],
      searchResults: [],
      loading: false,
      searched: false,
      checkAll: false,
      // 작업 모달
      showWorkModal: false,
      workIndex: 0,
      showLabelDetail: false,
      showCustomPrint: false,
      // 추가 작업내역(봉제 등) 메모
      workMemoText: '',
      workMemoCn: '',
      workMemoKo: '',
      workMemoActual: '',
      memoSaving: false,
      reqTranslating: false,
      // 프린트 옵션
      printQty: 1,
      // 인쇄 영역을 실제로 그릴 때만 true. 평소에 켜두면 수량만큼 바코드 SVG 가
      // 화면에 그려져 브라우저가 멈춘다(입고수량이 수백이면 기본값부터 그렇다)
      printReady: false,
      PRINT_QTY_MAX: 1000,
      printGap: 10,
      printFontSize: 100,
      printOpts: { sku: true, kc: false, food: false, barcode: true, label: false },
      printData: { barcode: '', sku: '', name: '', label: null },
      printAllData: [],
      // 韩文标签资料 — 지금 연 상품(화주·바코드)에 등록된 식검/KC 한글표시사항(없으면 null)
      masterLabel: null,
      LABEL_EXTRA_KEYS,
      customPrintContent: '',
      // 수동출력(상품 선택 없이)
      showManualPrint: false,
      manualTab: 0,
      manualBarcode: '',
      manualSku: '',
      manualBoxNo: '',
      manualBoxQty: 1,
      // 연속번호: 입력값 뒤에 001, 002 … 를 붙인다(서버 박스번호 자동생성과 같은 3자리)
      manualBoxSeq: false,
      manualBoxStart: 1,
      // 인쇄할 때만 채운다 — printReady 와 같은 이유(평소에 그려두면 수량만큼 SVG 가 쌓인다)
      manualBoxList: [],
      // AWS S3
      albumBucketName: 'elasticbeanstalk-ap-northeast-2-160290560268',
      bucketRegion: 'ap-northeast-2',
      IdentityPoolId: 'ap-northeast-2:19db1347-4000-4e66-973a-e2077f8cad1d',
      errorImageFiles: [],
      uploadingImages: false,
      // 입고오류
      showErrorModal: false,
      errorTarget: null,
      errorBulk: false,     // 일괄 오류 등록 중(#127)
      bulkTargets: [],
      bulkForms: {},        // 건(_id)별 입력값 — 옵션마다 따로 채운 뒤 한꺼번에 제출(#127 SYL)
      bulkOpenId: '',       // 지금 펼친 카드
      bulkUploading: {},
      errorMode: 'register', // 'register' | 'resolve'
      errorForm: { 오류유형: '', 오류내용: '', 오류수량: '', 출고가능수량: '', 미구성양품수량: '', 작업내용: '', 이미지URLs: [] },
      resolveForm: { 처리방법: '', 메모: '', 반품정보: { 수취인: '', 연락처: '', 주소: '', 운송장번호: '' }, 선택: '', 환불_CNY: '', 추가_CNY: '', 화주환불: true, 대상ids: [] },
      adjustInfo: null, // /import/error/adjust/info — 선택지·형제·견적 vs 실결제·현재 조정
      // 错误类型 10종(2026-10-18 SYL #71) + 예전 유형 8종을 그 아래에 이어서
      // 错误类型 13종(2026-10-18 SYL #71 최종) — 앞 5개(数量不足·数量超出·商品破损/不良·商家发错货·选项错发)는 입고 조정 자동 선택지가 붙은 예전 저장 키 그대로
      errorTypes: [
        { value: '', text: '请选择(선택하세요)' },
        { value: '수량부족', text: '1.数量不足' },
        { value: '수량초과', text: '2.数量超出' },
        { value: '상품불량', text: '3.商品破损/不良' },
        { value: '오배송', text: '4.商家发错货' },
        { value: '옵션뒤바뀜', text: '5.选项错发·多少互换' },
        { value: '与客户确认事项', text: '6.与客户确认事项' },
        { value: '与商家确认事项', text: '7.与商家确认事项' },
        { value: '条码问题', text: '8.条码问题' },
        { value: '此待办作废', text: '9.此待办作废' },
        { value: '到货少包裹，需要核实', text: '10.到货少包裹，需要核实' },
        { value: '退款/客户取消', text: '11.退款/客户取消' },
        { value: '未发货与客户确认事项', text: '12.未发货与客户确认事项' },
        { value: '办公室转达退款问题', text: '13.办公室转达退款问题' }
      ],
      resolveMethods: [
        { text: '入库完成处理(입고완료처리)', value: '입고완료처리' },
        { text: '退货(반품)', value: '반품' },
        { text: '废弃(폐기)', value: '폐기' },
        { text: '入库待发(입고대기)', value: '입고대기' },
        { text: '商家补发(판매자 재발송)', value: '판매자재발송' },
        { text: '换货(교환)', value: '교환' },
        // 끝내 안 온 수량을 더 기다리지 않고 닫는다 — 재고 0·출고/비용 대상 아님. 1688 금액은 그대로(2026-10-06 YZY)
        { text: '缺货结单(미도착 종결)', value: '미도착종결' }
      ],
      // 박스배정 모달
      showBoxModal: false,
      boxAssignItems: [],
      boxAssignCompanyid: ''
    };
  },
  computed: {
    adjustChoices() {
      return (this.adjustInfo && this.adjustInfo.선택지) || [];
    },
    selectedChoice() {
      return this.adjustChoices.find(c => c.key === this.resolveForm.선택) || null;
    },
    // 선택지를 고르면 처리방법은 선택지가 정한다(도착기준 = 들어온 게 있으면 입고완료처리, 없으면 미도착종결)
    effectiveMethod() {
      const c = this.selectedChoice;
      if (!c) return this.resolveForm.처리방법;
      if (c.처리방법 !== '도착기준') return c.처리방법;
      return this.adjustInfo && this.adjustInfo.이건 && this.adjustInfo.이건.입고수량 > 0 ? '입고완료처리' : '미도착종결';
    },
    // 연속번호 켰을 때 무엇이 찍힐지 미리 보여준다(첫 장 ~ 끝 장)
    manualBoxSeqPreview() {
      const boxNo = (this.manualBoxNo || '').trim();
      const qty = Math.min(this.PRINT_QTY_MAX, Math.max(1, parseInt(this.manualBoxQty) || 1));
      const list = this.manualBoxSeqList(boxNo, qty).map(v => v.replace(/\n/g, ' '));
      return list.length > 1 ? `${list[0]} ~ ${list[list.length - 1]} (${qty})` : list[0];
    },
    selectedItems() {
      return this.searchResults.filter(i => i._selected);
    },
    // 연 촬영 대기열 줄의 건이 검색 결과에 하나 이상 있고, 그 건이 전부 처리(입고·오류·이미 입고됨)됐는가.
    // 부분입고 조각은 원본 id 로 묶여 원본 행이 남은 수량을 들고 있으므로 원본 행 기준으로 본다
    photoQueueAllDone() {
      const q = this.photoQueueActive;
      if (!q) return false;
      const ids = (q.reqIds || []).map(String);
      const rows = this.searchResults.filter(s => ids.includes(String(s._id)));
      return rows.length > 0 && rows.every(s => s._done || this.isInboundDone(s) || s.processStatus === '입고오류');
    },
    currentItem() {
      return this.searchResults[this.workIndex] || null;
    },
    reqChips() {
      const r = (this.currentItem && this.currentItem.요청내역) || {};
      return REQ_CHIPS.map(c => ({ ...c, on: c.fields.some(f => !!r[f]) }));
    },
    // 要求事项 번역 대상: 화주가 한글로 적은 其他(物流)/其他(采购)
    hasReqContent() {
      const r = this.currentItem && this.currentItem.요청내역;
      return !!(r && ((r.reqcontent || '').trim() || (r.reqcontentPurchase || '').trim()));
    },
    doneCount() {
      return this.searchResults.filter(i => i._done).length;
    },
    companySummary() {
      const map = {};
      this.searchResults.forEach(i => {
        const c = i.companyid || '未指定(미지정)';
        if (!map[c]) map[c] = { done: 0, total: 0 };
        map[c].total++;
        if (i._done) map[c].done++;
      });
      return map;
    },
    hasNextUnprocessed() {
      return this.searchResults.some((item, idx) => idx !== this.workIndex && !item._done);
    },
    customPrintLines() {
      return this.customPrintContent ? this.customPrintContent.split('\n') : [];
    },
    // 일반 출력은 상품명을 「식품위생법에 의한 한글표시사항」 위에 둔다. 내용수정 출력도
    // 같은 모양이어야 해서 첫 줄을 위로 올린다(위 템플릿). 올린 줄은 본문에서 뺀다 —
    // 안 그러면 같은 줄이 두 번 찍힌다. 식품용이 꺼져 있으면 헤더가 없으므로 전부 본문이다.
    customPrintBodyLines() {
      return this.printOpts.food ? this.customPrintLines.slice(1) : this.customPrintLines;
    },
    // 로고 div 의 em 은 표시사항 영역 글자크기(printFontSize%) 적용 전 기준이라 여기서 환산한다
    labelLogoDropEm() {
      return +(LABEL_LOGO_DROP_LINES * 0.8 * 1.3 * (this.printFontSize || 100) / 100).toFixed(3);
    }
  },
  watch: {
    // 연 촬영 대기열 줄의 건을 다 처리하면 그 줄을 done(목록에서 빠진다)
    photoQueueAllDone(v) {
      if (!v || !this.photoQueueActive || !this.$refs.photoQueue) return;
      this.$refs.photoQueue.done(this.photoQueueActive._id);
    },
    // 처리 선택을 바꾸면 처리방법·기본 금액을 그 선택지에 맞춘다(결품 환불 = 이 건 견적 전액이 기본)
    'resolveForm.선택'(key) {
      const c = this.adjustChoices.find(x => x.key === key);
      if (!c) return;
      this.resolveForm.처리방법 = this.effectiveMethod;
      if (c.환불 && !c.환불선택 && this.resolveForm.환불_CNY === '' && this.adjustInfo && this.adjustInfo.이건 && c.key.indexOf('결품') === 0) {
        this.resolveForm.환불_CNY = this.adjustInfo.이건.견적_CNY || '';
      }
    },
    currentItem(item) {
      if (!item) return;
      // 바코드 출력 수량을 입고수량 기준으로 맞춘다 (기본 1장이라 매번 손으로 고쳐야 했다)
      this.syncPrintQty();
      // 食品用/KC는 상품마다 수동 옵션이므로 상품 바뀌면 초기화
      this.printOpts.food = false;
      this.printOpts.kc = false;
      // 韩文标签资料 에 이 화주·바코드가 있으면 「打印韩文标签」 버튼을 띄운다
      this.loadMasterLabel(item);
      // 요청내역에 따라 프린트 옵션 자동 설정
      if (item.요청내역) {
        // 한글표시사항은 따로 고르는 칸이 없다 — 구매요청의 「바코드라벨(한글표시사항 포함)」 하나로 정해진다.
        // 숨은 요청내역.한글표시사항 은 기본값·복사로 어긋나 있어(바코드 OFF 인데 true 등) 바코드 선택을 기준으로 삼는다
        this.printOpts.label = item.요청내역.barcode === true;
        this.printOpts.barcode = item.요청내역.barcode !== false;
      }
      // 추가 작업내역 메모를 현재 상품 기준으로 동기화
      this.workMemoText = (item.요청내역 && item.요청내역.workMemo) || '';
      this.workMemoCn = (item.요청내역 && item.요청내역.workMemo_cn) || '';
      this.workMemoKo = (item.요청내역 && item.요청내역.workMemo_ko) || '';
      this.workMemoActual = (item.요청내역 && item.요청내역.workMemoActual) || '';
    },
    // 입고수량을 고치면 출력 수량도 따라온다. 그 뒤 출력 수량을 직접 고치면 그 값이 남는다
    'currentItem._입고수량'() {
      this.syncPrintQty();
    },
    // 손으로 큰 수를 넣어도 막는다. 수천 장을 한 번에 그리면 인쇄 창까지 멈춘다
    printQty(v) {
      const n = parseInt(v);
      if (!n || n < 1) { this.printQty = 1; return; }
      if (n > this.PRINT_QTY_MAX) this.printQty = this.PRINT_QTY_MAX;
    }
  },
  methods: {
    // 샘플검증 → 화주에게 발송 건(2026-10-04 YZY): 화주주소·신고영문명·HS 를 창고에서도 본다.
    // 샘플검증 화면 저장 직후엔 sampleCheck 가 없고 reqtype '샘플검증' 이다. 폐기(destroy·distroy)는 제외.
    isSampleSend(r) {
      if (!r) return false;
      const 요청 = r.요청내역 || {};
      const sc = 요청.sampleCheck;
      const sample = r.reqtype === '샘플검증' || !!(sc && sc.enabled);
      if (!sample || 요청.destination === 'distroy') return false;
      return !(sc && sc.enabled && sc.disposition === 'destroy');
    },
    sampleAddrText(r) {
      const a = (r && r.요청내역 && r.요청내역.화주주소) || {};
      return [a.업체명, a.주소, a.연락처].map(v => (v || '').trim()).filter(Boolean).join(' / ');
    },
    // 쿠팡 supplierHub 이미지 주소의 "coupangcdn.com/image//" 이중 슬래시는 https 로 403 이 난다
    cdnImg(url) {
      if (typeof url !== 'string' || !url.includes('coupangcdn.com')) return url;
      return url
        .replace(/^http:\/\/([^/]*coupangcdn\.com)/, 'https://$1')
        .replace(/(coupangcdn\.com\/image)\/{2,}/, '$1/');
    },
    // 표시 전용 — 판매구성수량(단품/빈값=1)과 총 구매수량(구매요청수량x판매구성수량).
    // 입고수량 기본값·저장·인쇄 계산은 이 값을 쓰지 않는다.
    composeQty(item) {
      if (!item || !item.판매구성수량 || item.판매구성수량 === '단품') return 1;
      return parseInt(item.판매구성수량) || 1;
    },
    purchaseTotalQty(item) {
      return (parseInt(item && item.구매요청수량) || 0) * this.composeQty(item);
    },
    // 바코드는 판매단위(세트)당 1장이다. 입고수량·구매요청수량은 이미 세트 수라 그대로 쓴다.
    // 판매구성수량은 세트 한 개에 든 낱개 수다(1688 주문수량 = 구매요청수량 × 판매구성수량, 위 订单信息 참고).
    // 예전엔 여기서 한 번 더 나눠서 4P 세트 10개가 2장으로 나왔다. 전체출력(printAll)도 같은 규칙이다.
    syncPrintQty() {
      const item = this.currentItem;
      if (!item) return;
      const qty = parseInt(item._입고수량) || parseInt(item.구매요청수량) || 0;
      this.printQty = Math.min(this.PRINT_QTY_MAX, Math.max(1, qty));
    },
    openBoxAssign() {
      const targets = this.selectedItems.length ? this.selectedItems : this.searchResults.filter(i => !i._done && i.processStatus !== '입고오류');
      if (!targets.length) return alert('박스배정할 상품을 선택하세요.');
      const cids = [...new Set(targets.map(i => i.companyid))];
      if (cids.length > 1) return alert('박스는 화주별로 담아야 합니다. 같은 화주 상품만 선택하세요.');
      targets.forEach(i => { if (!i._입고수량) i._입고수량 = parseInt(i.구매요청수량) || 0; });
      this.boxAssignItems = targets;
      this.boxAssignCompanyid = cids[0] || '';
      this.showBoxModal = true;
    },
    onBoxAssignDone(results) {
      const originIds = (results || []).filter(r => r.isOrigin).map(r => r._id);
      this.searchResults.forEach(s => {
        if (originIds.includes(s._id)) { s._done = true; s._selected = false; }
      });
      this.showBoxModal = false;
      this.doSearch();
    },
    async revertSplit(item) {
      if (!confirm('이 입고·박스배정을 되돌리시겠습니까?\n분할된 항목이 하나로 병합되고 입고 전 상태로 돌아갑니다.')) return;
      this.loading = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/revertSplit', { splitGroupId: item.splitGroupId });
        if (data.요청결과 === '성공') {
          this.$bvToast.toast('되돌리기 완료 (병합수량: ' + data.입고수량 + ')', { title: '完成(완료)', variant: 'success', solid: true, toaster: 'b-toaster-bottom-left' });
          this.doSearch();
        } else {
          alert('되돌리기 실패: ' + (data.error && data.error.errorMessage || ''));
        }
      } catch (e) {
        console.error('revertSplit 오류:', e);
        alert('되돌리기 오류: ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    // 已跟客户确认 목록에서 订单号 를 누르면 입고처리 탭으로 바꿔 그 주문을 바로 검색한다(2026-10-04 YZY)
    openConfirmedOrder({ type, no }) {
      this.mainTab = 'import';
      this.searchType = type;
      this.searchKeyword = no;
      this.doSearch();
    },
    // 하단 촬영 대기열 줄을 누르면 — 그 단호로 검색해서 위에 연다(사진은 각 행 사진 칸에 이미 붙어 있다)
    async openPhotoQueue(row) {
      this.photoQueueActive = null;
      this.mainTab = 'import';
      this.searchType = row.검색타입;
      this.searchKeyword = row.번호;
      await this.doSearch();
      this.photoQueueActive = row;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onPhotoQueueDone(id) {
      if (this.photoQueueActive && this.photoQueueActive._id === id) this.photoQueueActive = null;
    },
    async doSearch() {
      if (!this.searchKeyword.trim()) {
        alert({ tracking: '请输入快递单号(운송장번호를 입력해주세요)', order: '请输入货主订单号(화주주문번호를 입력해주세요)', order1688: '请输入1688订单号(1688주문번호를 입력해주세요)' }[this.searchType]);
        return;
      }
      this.loading = true;
      this.searched = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/search', {
          // 구매취소된 건은 물건이 오지 않으므로 입고처리 목록에서 뺀다.
          // 같은 API 를 쓰는 구매요청 화면은 이 플래그를 안 보내므로 기존대로 전부 본다
          입고대상만: true,
          검색타입: this.searchType,
          검색어: this.searchKeyword.trim()
        });
        if (data.요청결과 === '성공') {
          this.searchResults = this.orderByGroup(data.results).map(r => ({
            ...r,
            _selected: false,
            _done: false,
            _입고수량: parseInt(r.입고수량) || parseInt(r.구매요청수량) || 0
          }));
        } else {
          alert(data.error?.errorMessage || '搜索失败(검색 실패)');
          this.searchResults = [];
        }
      } catch (e) {
        console.error('입고 검색 오류:', e);
        alert('搜索错误(검색 오류): ' + e.message);
        this.searchResults = [];
      } finally {
        this.loading = false;
      }
    },

    toggleAll() {
      this.searchResults.forEach(i => (i._selected = this.checkAll));
    },

    // 상태 표시는 중국어(저장 키는 한국어 그대로) — utils/coupassStatusText 한 곳에서
    statusText(status) {
      return processStatusText(status);
    },
    // 待确认出库 인데 处理状态=处理完成 으로 저장된 건은 「已跟客户确认待入库」(2026-10-04 YZY)
    itemStatusText(item) {
      const 요청 = (item && item.요청내역) || {};
      if (item && item.processStatus === '출고확인대기') return 요청.confirmStatus === '처리완료' ? '已跟客户确认待入库' : '待客户确认';
      return processStatusText(item && item.processStatus);
    },
    // 입고 버튼 대신 보이는 문구 — 출고확인대기(待客户确认)는 아직 화주 확인 단계라 「已入库」로 묶지 않는다(2026-10-04 YZY)
    inboundDoneText(item) {
      if (item && item.processStatus === '출고확인대기') return this.itemStatusText(item);
      if (item && item.processStatus === '미도착종결') return '缺货结单(미도착 종결)';
      return '已入库(입고됨)';
    },
    errorStatusText(status) {
      return errorStatusText(status);
    },
    statusVariant(status) {
      const map = { '배송완료': 'primary', '입고작업': 'warning', '배송중': 'info', '출고대기': 'success', '출고확인대기': 'warning', '재고대기': 'info', '입고오류': 'danger', '반품확인': 'warning', '폐기확인': 'dark', '입고대기': 'secondary', '반품완료': 'info', '폐기완료': 'dark', '미도착종결': 'dark' };
      return map[status] || 'secondary';
    },
    errorStatusVariant(status) {
      const map = { '접수': 'danger', '입고완료처리': 'success', '반품진행': 'warning', '폐기진행': 'dark', '입고대기': 'secondary', '판매자재발송': 'info', '교환진행': 'info', '미도착종결': 'dark' };
      return map[status] || 'secondary';
    },

    // ---- 작업 모달 ----
    openWorkModal() {
      this.workIndex = this.searchResults.findIndex(i => !i._done);
      if (this.workIndex < 0) this.workIndex = 0;
      this.showWorkModal = true;
    },
    openWorkModalAt(idx) {
      this.workIndex = idx;
      this.showWorkModal = true;
    },
    onWorkModalHidden() {
      this.showLabelDetail = false;
      this.showCustomPrint = false;
    },
    moveToNextUnprocessed() {
      const start = this.workIndex + 1;
      for (let i = start; i < this.searchResults.length; i++) {
        if (!this.searchResults[i]._done) { this.workIndex = i; return; }
      }
      for (let i = 0; i < start; i++) {
        if (!this.searchResults[i]._done) { this.workIndex = i; return; }
      }
    },

    // ---- 바코드 프린트 ----
    // 라벨 프린터는 라벨 한 장이 종이 한 장이다. 예전엔 라벨을 이어 붙여 흘려보내서
    // 종이 경계와 라벨 경계가 어긋나(다음 라벨 윗부분이 앞 장에 찍힘) 여러 장 출력이 밀렸다.
    // 그래서 라벨마다 페이지를 넘기고(템플릿 break-after), 인쇄 창의 기본 여백(@page·body)을 0 으로 둔다.
    // 종이 크기는 강제하지 않는다 — 프린터에 설정된 라벨 크기를 그대로 쓰므로 다른 크기 라벨도 영향 없다.
    // 박스라벨 = 가로 80mm × 세로 60mm 라벨지. 용지 크기를 지정해야 프린터 기본 용지 왼쪽에 쏠리지 않는다
    boxLabelFontSize,
    boxLabelPrintOptions() {
      // 바코드는 인쇄 창에서 높이 18mm 로 고정하고, 긴 값은 라벨 폭 92% 안으로 줄인다(벡터라 선명도 그대로)
      const css = '@page{size:80mm 60mm;margin:0}html,body{margin:0;padding:0}svg{height:18mm;width:auto;max-width:92%}';
      return { name: '_blank', autoClose: true, styles: ['data:text/css;charset=utf-8,' + encodeURIComponent(css)] };
    },
    labelPrintOptions() {
      const css = '@page{margin:0}html,body{margin:0;padding:0}';
      return { name: '_blank', autoClose: true, styles: ['data:text/css;charset=utf-8,' + encodeURIComponent(css)] };
    },
    printAllBarcodes() {
      if (!this.searchResults.length) return;
      // 각 상품별로 입고수량(=세트 수)장씩 연속 출력용 데이터 생성 — syncPrintQty 와 같은 규칙
      this.printAllData = [];
      this.searchResults.forEach(item => {
        const qty = Math.max(1, parseInt(item._입고수량) || parseInt(item.구매요청수량) || 0);
        const entry = {
          barcode: (item.바코드 || '').trim(),
          sku: item.SKUID || item._skuFromProductdb || '',
          name: item.상품명 || '',
          label: item.한글표시사항 || null
        };
        for (let i = 0; i < qty; i++) this.printAllData.push({ ...entry });
        this.logBarcodePrint(item, qty, true);
      });
      this.$nextTick(() => {
        this.$htmlToPaper('printAreaAll', this.labelPrintOptions());
        setTimeout(() => { this.printAllData = []; }, 2000);
      });
    },
    // 바코드 출력 기록(子账号管理 작업량 통계용) — 기다리지 않는다. 실패해도 출력은 그대로 된다
    logBarcodePrint(item, qty, all) {
      if (!item) return;
      axios.post('/api/coupass-staff/work-event', {
        qty: qty, barcode: (item.바코드 || '').trim(), companyid: item.companyid || '', reqId: item._id ? String(item._id) : '', all: !!all
      }).catch(e => console.error('바코드 출력 기록 실패:', e));
    },
    printBarcode() {
      if (!this.currentItem) return;
      this.logBarcodePrint(this.currentItem, this.printQty, false);
      this.printData = {
        barcode: (this.currentItem.바코드 || '').trim(),
        sku: this.currentItem.SKUID || this.currentItem._skuFromProductdb || '',
        name: this.currentItem.상품명 || '',
        label: this.currentItem.한글표시사항 || null
      };
      this.printReady = true;
      this.$nextTick(() => {
        this.$htmlToPaper('printArea', this.labelPrintOptions());
        // 인쇄 창이 내용을 다 읽은 뒤에 내린다. 바로 내리면 빈 종이가 나올 수 있다
        setTimeout(() => { this.printReady = false; }, 2000);
      });
    },
    openErrEdit(item) {
      const d = item.입고오류상세 || {};
      this.errEditTarget = item;
      this.errEditForm = { 오류내용: d.오류내용 || '', 오류수량: d.오류수량 === '' || d.오류수량 == null ? '' : d.오류수량 };
      this.showErrEdit = true;
    },
    // 바뀐 칸만 보낸다 — 권한(등록자 본인·같은 그룹 조장)은 서버가 판정한다. 代办列表 의 «修改» 와 같은 API
    async confirmErrEdit() {
      const item = this.errEditTarget;
      if (!item) return;
      const d = item.입고오류상세 || {};
      const body = { _id: item._id };
      if (this.errEditForm.오류내용 !== (d.오류내용 || '')) body.오류내용 = this.errEditForm.오류내용;
      const 이전수량 = d.오류수량 === '' || d.오류수량 == null ? '' : d.오류수량;
      if (!item.부분입고원본id && this.errEditForm.오류수량 !== 이전수량) body.오류수량 = this.errEditForm.오류수량;
      if (Object.keys(body).length < 2) { this.showErrEdit = false; return; }
      this.errEditing = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/error/edit', body);
        if (data.요청결과 !== '성공') {
          alert('保存失败(저장 실패): ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        if (!item.입고오류상세) this.$set(item, '입고오류상세', {});
        if ('오류내용' in body) this.$set(item.입고오류상세, '오류내용', body.오류내용);
        if ('오류수량' in body) this.$set(item.입고오류상세, '오류수량', body.오류수량);
        this.showErrEdit = false;
        this.$bvToast.toast('已保存', { title: '修改', variant: 'success', solid: true, autoHideDelay: 1500 });
      } catch (e) {
        console.error('입고오류 수정 오류:', e);
        alert('保存失败(저장 실패): ' + e.message);
      } finally {
        this.errEditing = false;
      }
    },
    async loadMasterLabel(item) {
      this.masterLabel = null;
      const companyid = item && item.companyid;
      const barcode = String((item && item.바코드) || '').replace(/\s+/g, '');
      if (!companyid || !barcode) return;
      try {
        const { data } = await axios.post('/api/coupassLabel/get', { companyid, barcode });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '查询失败');
        // 응답을 기다리는 사이 다른 상품으로 넘어갔으면 버린다
        if (this.currentItem !== item) return;
        const l = data.label;
        this.masterLabel = l && (l.food || l.kc || l.hyg) ? l : null;
      } catch (e) {
        console.error('韩文标签资料 조회 실패:', e);
      }
    },
    // 韩文标签资料 의 식검/KC 표시사항으로 출력 — 일반 출력(printArea)과 같은 템플릿·같은 인쇄 옵션
    printMasterLabel(kind) {
      const sec = this.currentItem && this.masterLabel && this.masterLabel[kind];
      if (!sec) return;
      this.logBarcodePrint(this.currentItem, this.printQty, false);
      this.printOpts.label = true;
      this.printOpts.barcode = true;
      this.printOpts.food = kind === 'food';
      this.printOpts.kc = kind === 'kc';
      // 위생검역은 식품용·KC 마크 없이 제목 + 항목 줄만 찍는다(출력 양식 = #printArea 의 printData.hyg 가지)
      const isHyg = kind === 'hyg';
      this.printData = {
        barcode: (this.currentItem.바코드 || '').trim(),
        sku: this.currentItem.SKUID || this.currentItem._skuFromProductdb || '',
        name: sec.제품명 || this.masterLabel.상품명 || this.currentItem.상품명 || '',
        label: sec,
        master: true,
        hyg: isHyg
      };
      this.printReady = true;
      this.$nextTick(() => {
        this.$htmlToPaper('printArea', this.labelPrintOptions());
        setTimeout(() => { this.printReady = false; }, 2000);
      });
    },
    // 韩文标签资料 출력은 빈 항목 줄을 뺀다(KC 라벨에 「내용량:」 같은 빈 줄이 찍히지 않게). 구매요청 값 출력은 지금처럼 전부
    labelRowShown(key) {
      return !this.printData.master || !!(this.printData.label && this.printData.label[key]);
    },
    printCustom() {
      if (!this.currentItem) return;
      this.logBarcodePrint(this.currentItem, this.printQty, false);
      this.printData = {
        barcode: (this.currentItem.바코드 || '').trim(),
        sku: this.currentItem.SKUID || this.currentItem._skuFromProductdb || '',
        name: this.currentItem.상품명 || '',
        label: null
      };
      this.printReady = true;
      this.$nextTick(() => {
        this.$htmlToPaper('printAreaCustom', this.labelPrintOptions());
        // 인쇄 창이 내용을 다 읽은 뒤에 내린다. 바로 내리면 빈 종이가 나올 수 있다
        setTimeout(() => { this.printReady = false; }, 2000);
      });
    },

    // ---- 수동출력 (상품 선택 없이) ----
    openManualPrint() {
      this.printQty = 1;
      this.showManualPrint = true;
    },
    // 내용수정 출력(#printAreaCustom)과 같은 라벨이다. 상품 대신 입력값을 printData 에 넣을 뿐
    printManualBarcode() {
      const barcode = (this.manualBarcode || '').trim();
      if (!barcode) return alert('请输入条码值(바코드 값을 입력하세요)');
      this.printOpts.barcode = true;
      this.printOpts.label = false;
      this.printData = { barcode, sku: (this.manualSku || '').trim(), name: '', label: null };
      this.printReady = true;
      this.$nextTick(() => {
        this.$htmlToPaper('printAreaCustom', this.labelPrintOptions());
        setTimeout(() => { this.printReady = false; }, 2000);
      });
    },
    // 박스배정의 라벨출력(BoxAssignModal printBox)과 같은 모양으로 찍는다
    printManualBox() {
      const boxNo = (this.manualBoxNo || '').trim();
      if (!boxNo) return alert('请输入箱号(박스번호를 입력하세요)');
      const qty = Math.min(this.PRINT_QTY_MAX, Math.max(1, parseInt(this.manualBoxQty) || 1));
      this.manualBoxQty = qty;
      this.manualBoxList = this.manualBoxSeq ? this.manualBoxSeqList(boxNo, qty) : Array(qty).fill(boxNo);
      this.$nextTick(() => {
        this.$htmlToPaper('boxLabelPrintAreaManual', this.boxLabelPrintOptions());
        setTimeout(() => { this.manualBoxList = []; }, 2000);
      });
    },

    // 여러 줄이면 마지막 줄 끝에 붙는다 — 서버 생성 형식(접두 + 줄바꿈 + 이니셜MMDD001)과 같은 자리
    manualBoxSeqList(boxNo, qty) {
      const start = Math.max(0, parseInt(this.manualBoxStart) || 0);
      return Array.from({ length: qty }, (_, i) => boxNo + String(start + i).padStart(3, '0'));
    },

    // ---- 추가 작업내역(봉제 등) 메모 ----
    _applyMemoToItem(field, value) {
      if (!this.currentItem) return;
      if (!this.currentItem.요청내역) this.$set(this.currentItem, '요청내역', {});
      this.$set(this.currentItem.요청내역, field, value);
    },
    async saveWorkMemo() {
      if (!this.currentItem || !this.workMemoText.trim()) return;
      this.memoSaving = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/updatereqcontent', {
          _id: this.currentItem._id, field: 'workMemo', value: this.workMemoText
        });
        if (data.success) {
          this._applyMemoToItem('workMemo', this.workMemoText);
          // 예전 追加作业内容 칸이라 제목이 「作业内容」 였다 — 지금 칸 이름으로(2026-10-04 YZY)
          // 알림은 왼쪽 아래 — 위 오른쪽(기본)이면 작업 창 关闭 버튼을 덮고, 오른쪽 아래는 입고 버튼 칸이다(2026-10-04 YZY). 이 화면 알림만 바꾼다(전역 위치는 그대로)
          this.$bvToast.toast('需跟客户确认内容已保存(고객 확인 필요 사항 저장 완료)', { title: '需跟客户确认(고객 확인 필요)', variant: 'success', solid: true, toaster: 'b-toaster-bottom-left' });
        } else {
          alert(data.message || '保存失败(저장 실패)');
        }
      } catch (e) {
        console.error('추가 작업내역 저장 오류:', e);
        alert('保存失败(저장 실패): ' + e.message);
      } finally {
        this.memoSaving = false;
      }
    },
    async saveWorkMemoActual() {
      if (!this.currentItem || !this.workMemoActual.trim()) return;
      this.memoSaving = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/updatereqcontent', {
          _id: this.currentItem._id, field: 'workMemoActual', value: this.workMemoActual
        });
        if (data.success) {
          this._applyMemoToItem('workMemoActual', this.workMemoActual);
          this.$bvToast.toast('实际追加作业内容已保存(실제 추가작업 내용 저장 완료)', { title: '实际追加作业内容(실제 추가작업)', variant: 'success', solid: true, toaster: 'b-toaster-bottom-left' });
        } else {
          alert(data.message || '保存失败(저장 실패)');
        }
      } catch (e) {
        console.error('실제 추가작업 저장 오류:', e);
        alert('保存失败(저장 실패): ' + e.message);
      } finally {
        this.memoSaving = false;
      }
    },
    // 要求事项 의 其他(物流)/其他(采购) 한→중 번역. 누를 때마다 다시 번역해 덮어쓴다
    async translateReqContent() {
      if (!this.hasReqContent) return;
      const r = this.currentItem.요청내역;
      const texts = [
        { key: 'reqcontent_cn', text: (r.reqcontent || '').trim() },
        { key: 'reqcontentPurchase_cn', text: (r.reqcontentPurchase || '').trim() }
      ].filter(t => t.text);
      this.reqTranslating = true;
      try {
        const { data } = await axios.post('/api/ai/translate-to-chinese', { texts });
        if (!(data && data.success && data.results)) {
          alert((data && data.error) || '翻译失败(번역 실패)');
          return;
        }
        for (const res of data.results) {
          const cn = (res.translated || '').trim();
          if (!cn) continue;
          await axios.post('/api/reqpurchase/updatereqcontent', { _id: this.currentItem._id, field: res.key, value: cn });
          this._applyMemoToItem(res.key, cn);
        }
        this.$bvToast.toast('翻译完成(번역 완료)', { title: '翻译(번역)', variant: 'info', solid: true, toaster: 'b-toaster-bottom-left' });
      } catch (e) {
        console.error('要求事项 번역 오류:', e);
        alert('翻译失败(번역 실패): ' + e.message);
      } finally {
        this.reqTranslating = false;
      }
    },

    // 부분 처리(입고·오류) 뒤 화면 반영 — 서버가 처리 수량만 새 건으로 떼어 냈다(splitOffPart).
    // 원래 건: 남은 수량·원래 상태로 그대로 두고 입고수량 칸을 남은 수량으로. 새 건: 원래 건 바로 아래 행으로 넣는다.
    insertSplitRow(origin, newId, qty, part, fields) {
      const groupId = part.부분입고원본id || origin._id;
      origin.구매요청수량 = part.남은수량;
      origin._입고수량 = part.남은수량;
      origin._selected = false;
      origin.부분입고원본id = groupId;
      const row = { ...origin, ...fields, _id: newId, 구매요청수량: qty, _입고수량: qty, 부분입고원본id: groupId, 입고사진: (origin.입고사진 || []).slice(), _selected: false };
      delete row.splitGroupId;
      const idx = this.searchResults.indexOf(origin);
      this.searchResults.splice(idx + 1, 0, row);
    },
    // 같은 구매요청에서 나뉜 건(부분입고원본id 묶음 — 없으면 자기 자신)의 상태별 수량. 한 건뿐이면 빈 배열
    // 오류 등록으로 원본에서 떼어낸 줄(입고오류 + 원본이 다른 문서) — 입고처리 목록에서는 숨기고 代办 목록에서 본다
    isSplitErrorRow(item) {
      return !!item && item.processStatus === '입고오류' && !!item.부분입고원본id && String(item.부분입고원본id) !== String(item._id);
    },
    canRegisterErrorAfterInbound(item) {
      return !!item && item.processStatus === '출고대기' && !(item.출고정보 && item.출고정보.boxLabelId);
    },
    isInboundDone(item) {
      const s = item && (item.processStatus || item.진행현황);
      return !!s && s !== '입고대기' && STATUS_ORDER.indexOf(s) >= INBOUND_DONE_FROM;
    },
    groupDocs(item) {
      const key = item.부분입고원본id || item._id;
      return this.searchResults.filter(s => (s.부분입고원본id || s._id) === key);
    },
    isGroupHead(item) {
      return (item.부분입고원본id || item._id) === item._id && this.groupDocs(item).length > 1;
    },
    isGroupPiece(item) {
      return !!item.부분입고원본id && item.부분입고원본id !== item._id && this.groupDocs(item).length > 1;
    },
    groupLineStyle(item) {
      if (this.isGroupHead(item)) return 'border-left:4px solid #17a2b8';
      if (this.isGroupPiece(item)) return 'border-left:4px solid #b8e3ea';
      return '';
    },
    // 검색 결과를 묶음끼리 붙인다 — 원본(남은 수량) 행 바로 아래에 조각 행. 묶음 밖 행 순서는 그대로
    orderByGroup(list) {
      const heads = new Map();
      list.forEach(r => { const k = r.부분입고원본id || r._id; if (!heads.has(k)) heads.set(k, []); heads.get(k).push(r); });
      const out = [];
      heads.forEach(g => {
        const head = g.find(r => r._id === (r.부분입고원본id || r._id));
        if (head) out.push(head);
        g.forEach(r => { if (r !== head) out.push(r); });
      });
      return out;
    },
    groupBadges(item) {
      if (!this.isGroupHead(item)) return [];
      const docs = this.groupDocs(item);
      const ERR = ['입고오류', '반품확인', '반품완료', '폐기확인', '폐기완료'];
      const WAIT = ['구매완료', '배송중', '배송완료', '입고대기', '구매확인', '입금완료', '입금요청'];
      const bucket = s => (s === '미도착종결' ? 'closed' : ERR.includes(s) ? 'err' : (WAIT.includes(s) || !s) ? 'wait' : 'in');
      const sum = { in: 0, wait: 0, err: 0, closed: 0 };
      docs.forEach(d => { sum[bucket(d.processStatus)] += parseInt(d.구매요청수량) || 0; });
      return [
        { key: 'in', text: '已入库(입고) ' + sum.in, variant: 'success', n: sum.in },
        { key: 'wait', text: '未到(미도착) ' + sum.wait, variant: 'secondary', n: sum.wait },
        { key: 'err', text: '错误(오류) ' + sum.err, variant: 'danger', n: sum.err },
        { key: 'closed', text: '缺货结单(미도착 종결) ' + sum.closed, variant: 'dark', n: sum.closed }
      ].filter(b => b.n > 0);
    },

    // 待客户确认 직전 — 작업 창의 需跟客户确认 칸을 적고 保存 을 안 눌렀으면 먼저 저장한다. 실패하면 false(입고하지 않는다)
    async saveUnsavedConfirmMemo(items) {
      const it = this.currentItem;
      if (!it || !items.includes(it)) return true;
      const text = (this.workMemoText || '').trim();
      const saved = ((it.요청내역 && it.요청내역.workMemo) || '').trim();
      if (!text || text === saved) return true;
      try {
        const { data } = await axios.post('/api/reqpurchase/updatereqcontent', { _id: it._id, field: 'workMemo', value: this.workMemoText });
        if (!data.success) {
          alert('需跟客户确认保存失败(확인 내용 저장 실패): ' + (data.message || ''));
          return false;
        }
        this._applyMemoToItem('workMemo', this.workMemoText);
        return true;
      } catch (e) {
        console.error('待客户确认 전 확인내용 저장 오류:', e);
        alert('需跟客户确认保存失败(확인 내용 저장 실패): ' + e.message);
        return false;
      }
    },
    // ---- 입고처리 ----
    // toStatus: 기본 출고대기. 「待客户确认」 버튼은 출고확인대기 로 보낸다.
    // 반환: 서버가 처리했으면 true, 취소·실패면 false — 작업 창은 true 일 때만 다음 건으로 넘어간다
    async processImport(items, toStatus = '출고대기') {
      if (!items.length) return false;
      let ok;
      if (toStatus === '출고확인대기') {
        // 2026-10-04 YZY: 需跟客户确认 를 적고 이 버튼을 눌렀는데 서버에 입고 요청이 한 건도 오지 않았다(로그·DB 확인).
        // 브라우저 기본 확인창(window.confirm)에서 끊긴 것으로 본다 — 화면 안 확인창으로 바꾸고, 저장 안 한 확인 내용은 먼저 저장한다
        ok = await this.$bvModal.msgBoxConfirm(`入库并转为待客户确认 ${items.length}件？(${items.length}건을 입고하고 고객확인대기로 보내시겠습니까?)`, {
          title: '⏳ 待客户确认(고객확인대기)', okVariant: 'warning', okTitle: '确认(확인)', cancelTitle: '取消(취소)', centered: true
        }).catch(e => { console.error('待客户确认 확인창 오류:', e); return false; });
        if (ok && !(await this.saveUnsavedConfirmMemo(items))) return false;
      } else {
        ok = window.confirm(`确认入库处理${items.length}件？(${items.length}건을 입고처리 하시겠습니까?)`);
      }
      if (!ok) return false;
      this.loading = true;
      try {
        const payload = items.map(i => ({
          _id: i._id,
          입고수량: i._입고수량 || parseInt(i.구매요청수량) || 0,
          toStatus
        }));
        const { data } = await axios.post('/api/reqpurchase/import/process', { items: payload });
        if (data.요청결과 === '성공') {
          this.$bvToast.toast(toStatus === '출고확인대기'
            ? `${data.results.length}件 已转为待客户确认，请在「待客户确认」菜单查看(고객확인대기로 보냄 — 「待客户确认」 메뉴)`
            : `${data.results.length}件 入库处理完成(입고처리 완료)`, {
            title: '入库处理(입고처리)', variant: 'success', solid: true, toaster: 'b-toaster-bottom-left'
          });
          data.results.forEach(r => {
            if (r.부분입고) {
              // 부분입고 — 입고한 수량은 새 건(출고대기)으로 떨어지고, 원래 건은 남은 수량으로 버튼이 남는다
              const origin = this.searchResults.find(s => s._id === r.부분입고.원본id);
              if (origin) this.insertSplitRow(origin, r._id, r.입고수량, r.부분입고, { processStatus: r.toStatus, 입고수량: r.입고수량, _done: true });
              return;
            }
            const found = this.searchResults.find(s => s._id === r._id);
            if (found) {
              found.processStatus = r.toStatus;
              found.입고수량 = r.입고수량;
              found._selected = false;
              found._done = true;
            }
          });
          return true;
        }
        alert(data.error?.errorMessage || '入库处理失败(입고처리 실패)');
        return false;
      } catch (e) {
        console.error('입고처리 오류:', e);
        alert('入库处理错误(입고처리 오류): ' + e.message);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async processImportAndNext(items, toStatus) {
      // 취소·실패면 그 자리에 둔다 — 예전엔 확인창을 닫아도 다음 건으로 넘어가 처리된 것처럼 보였다(2026-10-04 YZY)
      if (!(await this.processImport(items, toStatus))) return;
      if (this.hasNextUnprocessed) {
        this.moveToNextUnprocessed();
      } else if (this.doneCount === this.searchResults.length) {
        this.$bvToast.toast('所有商品入库完成!(모든 아이템 입고 완료!)', { title: '完成(완료)', variant: 'success', solid: true, toaster: 'b-toaster-bottom-left' });
      }
    },

    // ---- 입고오류 ----
    openErrorModal(item) {
      this.errorBulk = false;
      this.bulkTargets = [];
      this.errorTarget = item;
      this.errorImageFiles = [];
      this.adjustInfo = null;
      // 입고 조정으로 처리한 건도 이 창에서 고른 처리를 보고 되돌린다
      if (item.입고오류상세 && (item.processStatus === '입고오류' || item.입고오류상세.조정)) {
        this.errorMode = 'resolve';
        this.resolveForm = { 처리방법: '', 메모: '', 반품정보: { 수취인: '', 연락처: '', 주소: '', 운송장번호: '' }, 선택: '', 환불_CNY: '', 추가_CNY: '', 화주환불: true, 대상ids: [] };
        this.loadAdjustInfo(item);
      } else {
        this.errorMode = 'register';
        this.errorForm = { 오류유형: '', 오류내용: '', 오류수량: '', 출고가능수량: '', 미구성양품수량: '', 작업내용: '', 이미지URLs: [] };
      }
      this.showErrorModal = true;
    },
    // 처리 창 — 선택지(오류유형별)·같은 1688 주문 형제·견적 vs 실결제·현재 조정. 기본 선택지를 미리 고른다
    async loadAdjustInfo(item) {
      try {
        const { data } = await axios.post('/api/reqpurchase/import/error/adjust/info', { _id: item._id });
        if (data.요청결과 !== '성공') {
          console.error('입고조정 정보 실패:', data.error);
          return;
        }
        if (this.errorTarget !== item) return;
        this.adjustInfo = data;
        // 뒤바뀜 상대 = 같은 1688 주문에서 주문보다 많이 온 상품
        this.resolveForm.대상ids = data.형제.filter(s => s.입고수량 > s.구매요청수량).map(s => s._id);
        const def = data.선택지.find(c => c.기본);
        if (!data.조정 && def) this.resolveForm.선택 = def.key;
      } catch (e) {
        console.error('입고조정 정보 오류:', e);
      }
    },
    priceVarianceText(v) {
      return { 재청구필요: '实付更高，需重新开报价单(실결제가 더 높음 — 재청구 필요)', 유지: '实付更低，原报价单不变(낮아짐 — 기존 청구서 유지)', 같음: '一致(일치)' }[v] || v;
    },
    async undoAdjust() {
      if (!this.adjustInfo || !this.adjustInfo.조정) return;
      if (!confirm('撤销这次调整？库存·报价·成本会恢复，可重新选择(이 조정을 되돌릴까요? 재고·견적·원가가 처리 전으로 돌아갑니다)')) return;
      this.loading = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/error/adjust/undo', { _id: this.errorTarget._id });
        if (data.요청결과 !== '성공') {
          alert(data.error?.errorMessage || '撤销失败(되돌리기 실패)');
          return;
        }
        const found = this.searchResults.find(s => s._id === this.errorTarget._id) || this.errorTarget;
        found.processStatus = data.status;
        if (found.입고오류상세) this.$delete(found.입고오류상세, '조정');
        this.$bvToast.toast('已撤销调整(조정 되돌림)', { title: '错误处理(오류처리)', variant: 'info', solid: true, toaster: 'b-toaster-bottom-left' });
        this.showErrorModal = false;
      } catch (e) {
        console.error('입고조정 되돌리기 오류:', e);
        alert('撤销失败(되돌리기 실패): ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    // 핸드폰으로 찍은 오류사진 — 등록 시 기존 이미지 목록에 같이 실린다
    onErrorPhotos(urls) {
      urls.forEach(u => { if (!this.errorForm.이미지URLs.includes(u)) this.errorForm.이미지URLs.push(u); });
    },
    // 오류 창에서 지운 핸드폰 사진은 등록 대기 목록에서도 뺀다
    onErrorPhotoRemoved(url) {
      this.errorForm.이미지URLs = this.errorForm.이미지URLs.filter(u => u !== url);
    },
    onErrorFilesSelected(e) {
      this.errorImageFiles = Array.from(e.target.files || []);
      this.uploadErrorImages(); // 고르면 바로 올린다 — 올라가면 아래에 미리보기가 뜬다
    },
    // 错误类型 선택 색 — 목록 순서(첫 «선택» 제외)대로 팔레트를 돌려 쓴다
    errTypeStyle(value, opts) {
      const pal = [["#1b7f3b","#dff3e4"],["#a21caf","#fae8ff"],["#c2410c","#ffedd5"],["#1d4ed8","#dbeafe"],["#0f766e","#ccfbf1"],["#be185d","#fce7f3"],["#a16207","#fef3c7"],["#4338ca","#e0e7ff"],["#b91c1c","#fee2e2"],["#334155","#e2e8f0"]];
      const i = (opts || []).filter(o => o.value).findIndex(o => o.value === value);
      if (i < 0) return {};
      const [c, b] = pal[i % pal.length];
      return { color: c, background: b, fontWeight: 700, fontFamily: '"Microsoft YaHei", "微软雅黑", sans-serif' };
    },
    isVideoUrl(u) {
      return /\.(mp4|mov|m4v|webm|avi|mkv)(\?|$)/i.test(String(u || ''));
    },
    // 올린 것을 목록에서 뺀다(S3 객체는 그대로 둔다 — 이 등록에서만 빠진다)
    removeErrorUrl(url) {
      this.errorForm.이미지URLs = this.errorForm.이미지URLs.filter(u => u !== url);
    },
    uploadErrorImages() {
      if (!this.errorImageFiles.length) return;
      this.uploadingImages = true;
      AWS.config.update({
        region: this.bucketRegion,
        credentials: new AWS.CognitoIdentityCredentials({ IdentityPoolId: this.IdentityPoolId })
      });
      const s3 = new AWS.S3({ apiVersion: '2006-03-01', params: { Bucket: this.albumBucketName } });
      const uploads = this.errorImageFiles.map(file => {
        const key = `import-error/${Date.now()}_${file.name}`;
        return s3.upload({ Key: key, Body: file, ACL: 'public-read' }).promise();
      });
      Promise.all(uploads).then(results => {
        results.forEach(r => this.errorForm.이미지URLs.push(r.Location));
        this.$bvToast.toast(`${results.length}个文件上传完成(파일 업로드 완료)`, { title: '上传(업로드)', variant: 'success', solid: true, toaster: 'b-toaster-bottom-left' });
        this.errorImageFiles = [];
        if (this.$refs.errorFileInput) this.$refs.errorFileInput.value = '';
      }).catch(err => {
        console.error('S3 업로드 오류:', err);
        alert('上传失败(업로드 실패): ' + err.message);
      }).finally(() => { this.uploadingImages = false; });
    },
    // 일괄 오류 등록 — 고른 건 중 입고 전·오류 아닌 건만. 창을 열면 같은 입력칸을 쓴다
    openBulkError() {
      const targets = this.selectedItems.filter(i => !i._done && i.processStatus !== '입고오류' && !this.isInboundDone(i) && !(i.입고오류상세 && i.입고오류상세.조정));
      if (!targets.length) { alert('没有可登记错误的商品(오류를 등록할 수 있는 건이 없습니다 — 이미 입고·오류 처리된 건은 제외)'); return; }
      this.openErrorModal(targets[0]);
      this.errorBulk = true;
      this.bulkTargets = targets;
      const forms = {};
      targets.forEach(t => { forms[t._id] = { 오류유형: '', 오류내용: '', 오류수량: '', 출고가능수량: '', 미구성양품수량: '', 작업내용: '', 이미지URLs: [] }; });
      this.bulkForms = forms;
      this.bulkUploading = {};
      this.bulkOpenId = targets[0]._id;
    },
    // 카드 머리에 보일 옵션 — 중문 속성 + 한글 속성
    bulkOptionText(t) {
      const zh = [t.옵션1_중국어, t.옵션2_중국어].filter(Boolean).join(' / ');
      const ko = t.옵션 || '';
      return [zh ? '中文: ' + zh : '', ko ? '한글: ' + ko : ''].filter(Boolean).join('  ·  ');
    },
    bulkDone(t) { const f = this.bulkForms[t._id]; return !!(f && f.오류유형); },
    bulkCardClass(t) { return this.bulkDone(t) ? 'border-success' : ''; },
    copyPrevBulk(ti) {
      const prev = this.bulkForms[this.bulkTargets[ti - 1]._id];
      const cur = this.bulkForms[this.bulkTargets[ti]._id];
      if (!prev || !cur) return;
      ['오류유형', '오류내용', '출고가능수량', '미구성양품수량', '작업내용'].forEach(k => { cur[k] = prev[k]; });
    },
    removeBulkUrl(t, url) {
      const f = this.bulkForms[t._id];
      if (f) f.이미지URLs = f.이미지URLs.filter(u => u !== url);
    },
    // 옵션별 사진 업로드 — 단건 업로드와 같은 S3 방식
    onBulkFiles(t, e) {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      const input = e.target;
      this.$set(this.bulkUploading, t._id, true);
      AWS.config.update({
        region: this.bucketRegion,
        credentials: new AWS.CognitoIdentityCredentials({ IdentityPoolId: this.IdentityPoolId })
      });
      const s3 = new AWS.S3({ apiVersion: '2006-03-01', params: { Bucket: this.albumBucketName } });
      Promise.all(files.map(file => s3.upload({ Key: `import-error/${Date.now()}_${file.name}`, Body: file, ACL: 'public-read' }).promise()))
        .then(results => {
          const f = this.bulkForms[t._id];
          if (f) results.forEach(r => f.이미지URLs.push(r.Location));
          input.value = '';
        })
        .catch(err => {
          console.error('S3 업로드 오류(일괄):', err);
          alert('上传失败(업로드 실패): ' + err.message);
        })
        .finally(() => { this.$set(this.bulkUploading, t._id, false); });
    },
    // 옵션별로 채운 값을 한꺼번에 제출 — 건마다 기존 등록 API 를 차례로 부른다(서버 변경 없음)
    async submitBulkError() {
      const missing = this.bulkTargets.find(t => !(this.bulkForms[t._id] && this.bulkForms[t._id].오류유형));
      if (missing) { this.bulkOpenId = missing._id; alert('请先选择错误类型(오류유형이 비어 있는 옵션이 있습니다): ' + (this.bulkOptionText(missing) || missing.상품명)); return; }
      if (!confirm('将 ' + this.bulkTargets.length + ' 件按各自内容登记入库错误? (선택한 ' + this.bulkTargets.length + '건을 옵션별 내용대로 등록합니다)')) return;
      this.loading = true;
      const fails = [];
      const okIds = [];
      try {
        for (const t of this.bulkTargets) {
          const f = this.bulkForms[t._id];
          const 이미지목록 = [...f.이미지URLs];
          try {
            const { data } = await axios.post('/api/reqpurchase/import/error', {
              _id: t._id,
              오류유형: f.오류유형,
              오류내용: f.오류내용,
              오류수량: f.오류수량,
              출고가능수량: f.출고가능수량,
              미구성양품수량: f.미구성양품수량,
              작업내용: f.작업내용,
              이미지목록
            });
            if (data.요청결과 !== '성공') { fails.push((this.bulkOptionText(t) || t.상품명 || t._id) + ': ' + ((data.error && data.error.errorMessage) || '')); continue; }
            const found = this.searchResults.find(s => s._id === t._id);
            const 상세 = { 오류유형: f.오류유형, 오류상태: '접수', 오류내용: f.오류내용, 이미지: 이미지목록, 가격: '', 오류수량: f.오류수량, 출고가능수량: f.출고가능수량, 미구성양품수량: f.미구성양품수량, 작업내용: f.작업내용 };
            if (found && data.부분) {
              this.insertSplitRow(found, data._id, parseInt(f.오류수량) || 0, data.부분, { processStatus: '입고오류', 입고오류상세: 상세, _done: true });
            } else if (found) {
              found.processStatus = '입고오류';
              found.입고오류상세 = 상세;
              found._done = true;
              found._selected = false;
            }
            okIds.push(t._id);
          } catch (e) {
            console.error('일괄 입고오류 등록 오류:', t._id, e);
            fails.push((this.bulkOptionText(t) || t.상품명 || t._id) + ': ' + e.message);
          }
        }
      } finally {
        this.loading = false;
      }
      if (okIds.length) this.$bvToast.toast('批量登记完成(일괄 등록 완료) ' + okIds.length + '件', { title: '入库错误(입고오류)', variant: 'warning', solid: true, toaster: 'b-toaster-bottom-left' });
      if (fails.length) {
        // 성공한 건은 카드에서 빼고 실패한 건만 남겨 고쳐서 다시 제출할 수 있게 한다
        this.bulkTargets = this.bulkTargets.filter(t => !okIds.includes(t._id));
        this.bulkOpenId = this.bulkTargets.length ? this.bulkTargets[0]._id : '';
        alert('部分失败(일부 실패 ' + fails.length + '건 — 아래 건은 그대로 남겨 두었습니다):' + String.fromCharCode(10) + fails.slice(0, 5).join(String.fromCharCode(10)));
        return;
      }
      this.showErrorModal = false;
      this.errorBulk = false;
      this.bulkTargets = [];
      this.bulkForms = {};
    },
    async submitError() {
      if (!this.errorForm.오류유형) { alert('请选择错误类型(오류유형을 선택해주세요)'); return; }
      this.loading = true;
      try {
        // 사진은 업로드·핸드폰 촬영으로만 받는다(URL 직접 입력칸 제거). 가격은 입고오류 리스트에서 넣는다(2026-10-03 YZY)
        const 이미지목록 = [...this.errorForm.이미지URLs];
        const { data } = await axios.post('/api/reqpurchase/import/error', {
          _id: this.errorTarget._id,
          오류유형: this.errorForm.오류유형,
          오류내용: this.errorForm.오류내용,
          오류수량: this.errorForm.오류수량,
          출고가능수량: this.errorForm.출고가능수량,
          미구성양품수량: this.errorForm.미구성양품수량,
          작업내용: this.errorForm.작업내용,
          이미지목록
        });
        if (data.요청결과 === '성공') {
          this.$bvToast.toast('入库错误登记完成(입고오류 등록 완료)', { title: '入库错误(입고오류)', variant: 'warning', solid: true, toaster: 'b-toaster-bottom-left' });
          const found = this.searchResults.find(s => s._id === this.errorTarget._id);
          const 상세 = { 오류유형: this.errorForm.오류유형, 오류상태: '접수', 오류내용: this.errorForm.오류내용, 이미지: 이미지목록, 가격: '', 오류수량: this.errorForm.오류수량, 출고가능수량: this.errorForm.출고가능수량, 미구성양품수량: this.errorForm.미구성양품수량, 작업내용: this.errorForm.작업내용, 반품정보: {}, 처리이력: [{ 상태: '접수', 처리일: Date.now(), 처리자: '', 메모: this.errorForm.오류유형 }] };
          if (found && data.부분) {
            // 오류 수량만 떼어 낸 새 건이 입고오류 — 원래 건은 남은 수량으로 입고·오류 등록을 계속한다
            this.insertSplitRow(found, data._id, parseInt(this.errorForm.오류수량) || 0, data.부분, { processStatus: '입고오류', 입고오류상세: 상세, _done: true });
          } else if (found) {
            found.processStatus = '입고오류';
            found.입고오류상세 = 상세;
            found._done = true;
          }
          this.showErrorModal = false;
          if (this.showWorkModal && !data.부분 && this.hasNextUnprocessed) this.moveToNextUnprocessed();
        } else {
          alert(data.error?.errorMessage || '错误登记失败(오류 등록 실패)');
        }
      } catch (e) {
        console.error('입고오류 등록 오류:', e);
        alert('错误登记失败(오류 등록 실패): ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    async submitResolve() {
      const 선택 = this.resolveForm.선택;
      const c = this.selectedChoice;
      if (!선택 && !this.resolveForm.처리방법) { alert('请选择处理方式(처리방법을 선택해주세요)'); return; }
      // 선택지로 처리하면 선택 자체가 사유다 — 메모 필수는 「상태만 변경」의 缺货结单 에만
      if (!선택 && this.resolveForm.처리방법 === '미도착종결' && !String(this.resolveForm.메모 || '').trim()) { alert('请在处理备注填写缺货结单原因(처리 메모에 미도착 종결 사유를 입력해주세요)'); return; }
      if (c && (c.재배분 || c.초과반품) && !this.resolveForm.대상ids.length) { alert('请选择对应商品(상대 상품을 골라주세요)'); return; }
      const method = this.effectiveMethod;
      this.loading = true;
      try {
        const payload = {
          _id: this.errorTarget._id,
          처리방법: method,
          메모: this.resolveForm.메모
        };
        if (선택) {
          payload.선택 = 선택;
          payload.환불_CNY = this.resolveForm.환불_CNY;
          payload.추가_CNY = this.resolveForm.추가_CNY;
          payload.화주환불 = this.resolveForm.화주환불;
          payload.대상ids = this.resolveForm.대상ids;
        }
        if (method === '반품' || method === '교환') {
          payload.반품정보 = this.resolveForm.반품정보;
        }
        const { data } = await axios.post('/api/reqpurchase/import/error/resolve', payload);
        if (data.요청결과 === '성공') {
          this.$bvToast.toast('错误处理完成(오류 처리 완료): ' + (c ? c.라벨 : method), { title: '错误处理(오류처리)', variant: 'info', solid: true, toaster: 'b-toaster-bottom-left' });
          const found = this.searchResults.find(s => s._id === this.errorTarget._id);
          if (found) {
            found.processStatus = data.toStatus;
            found._done = true;
            if (found.입고오류상세) {
              const statusMap = { '입고완료처리': '입고완료처리', '반품': '반품진행', '폐기': '폐기진행', '입고대기': '입고대기', '판매자재발송': '판매자재발송', '교환': '교환진행', '미도착종결': '미도착종결' };
              found.입고오류상세.오류상태 = statusMap[data.조정 ? data.조정.처리방법 : method];
              if (data.조정) this.$set(found.입고오류상세, '조정', data.조정);
            }
          }
          this.showErrorModal = false;
          if (this.showWorkModal && this.hasNextUnprocessed) this.moveToNextUnprocessed();
        } else {
          alert(data.error?.errorMessage || '错误处理失败(오류 처리 실패)');
        }
      } catch (e) {
        console.error('오류 처리 실패:', e);
        alert('错误处理失败(오류 처리 실패): ' + e.message);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style>
/* 错误类型 드롭다운 — 항목마다 색(글자+배경), 선택한 뒤 닫힌 상태에서도 그 색 유지, 微软雅黑 굵게 (2026-10-18 SYL) */
.err-type-select, .err-type-select option { font-family: "Microsoft YaHei", "微软雅黑", sans-serif !important; font-weight: 700; }
.err-type-select option:nth-child(10n+2) { color: #1b7f3b; background: #dff3e4; }
.err-type-select option:nth-child(10n+3) { color: #a21caf; background: #fae8ff; }
.err-type-select option:nth-child(10n+4) { color: #c2410c; background: #ffedd5; }
.err-type-select option:nth-child(10n+5) { color: #1d4ed8; background: #dbeafe; }
.err-type-select option:nth-child(10n+6) { color: #0f766e; background: #ccfbf1; }
.err-type-select option:nth-child(10n+7) { color: #be185d; background: #fce7f3; }
.err-type-select option:nth-child(10n+8) { color: #a16207; background: #fef3c7; }
.err-type-select option:nth-child(10n+9) { color: #4338ca; background: #e0e7ff; }
.err-type-select option:nth-child(10n+10) { color: #b91c1c; background: #fee2e2; }
.err-type-select option:nth-child(10n+11) { color: #334155; background: #e2e8f0; }
.modal-fullscreen {
  max-width: 98vw !important;
  margin: 1vh auto !important;
  height: 98vh;
}
.modal-fullscreen .modal-content {
  height: 98vh;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
}
.modal-fullscreen .modal-body {
  flex: 1;
  overflow: hidden;
}
</style>

